type BusinessCenterEntityId = string;

interface BusinessCenterCompanyRecord {
  id: BusinessCenterEntityId;
  parentCompanyId?: BusinessCenterEntityId | null;
  companyName: string;
  companySlug: string;
  companyType: "parent" | "subsidiary" | "client_company";
  brand: "kreatethedate" | "truevance_connect" | string;
  status: "active" | "inactive" | "archived";
  createdAt: string;
  updatedAt: string;
}

export type ARRecoverySectionKey =
  | "dashboard"
  | "client-management"
  | "client-exchange"
  | "customer-account-management"
  | "customer-accounts"
  | "aging-management"
  | "promise-to-pay"
  | "smart-tickler"
  | "collection-letters"
  | "document-center"
  | "statements"
  | "payment-center"
  | "settlement-center"
  | "billing-center"
  | "recovery-activity"
  | "recovery-reporting"
  | "recovery-analytics"
  | "manager-command-center"
  | "delivery-operations"
  | "client-portal"
  | "white-label-settings"
  | "commercialization-center";

export interface ARRecoverySectionLink {
  key: ARRecoverySectionKey;
  label: string;
  href: string;
  description: string;
  state: "Active" | "Ready" | "Planned";
}

export interface ARRecoveryCompanyBranding {
  displayName: string;
  headline: string;
  primaryColor: string;
  accentColor: string;
  surfaceColor: string;
}

export interface ARRecoveryCustomerAccountRecord {
  id: BusinessCenterEntityId;
  companyId: BusinessCenterEntityId;
  customerName: string;
  customerNumber: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  agingSummary: string;
  openInvoiceCount: number;
  openBalanceCents: number;
  promiseToPayCount: number;
  recoveryStatus: "Current" | "At Risk" | "Escalated" | "Payment Plan" | "Resolved";
  assignedCollector: string;
  escalationStatus: "None" | "Watch" | "Escalated" | "Director Review";
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface ARRecoveryAgingBucketRecord {
  label: "Current" | "1-30 Days" | "31-60 Days" | "61-90 Days" | "90+ Days";
  balanceCents: number;
  accountCount: number;
  trendNote: string;
}

export interface ARRecoveryPromiseRecord {
  id: BusinessCenterEntityId;
  companyId: BusinessCenterEntityId;
  accountId: BusinessCenterEntityId;
  customerName: string;
  customerNumber: string;
  clientCompany: string;
  invoiceLabels: string[];
  remainingBalanceCents: number;
  promiseDate: string;
  amountCents: number;
  status: "Open" | "Pending" | "Due Today" | "Paid" | "Partial" | "Broken" | "Cancelled" | "Disputed" | "Escalated" | "Resolved" | "Upcoming" | "Completed";
  promiseType: "Single Payment" | "Partial Payment" | "Installment Plan" | "Settlement Agreement" | "Custom Promise";
  followUpDate?: string | null;
  collector: string;
  manager: string;
  createdAt: string;
  updatedAt: string;
  notes: string;
  collectorNotes: string;
  customerNotes: string;
  managerNotes: string;
  escalationNotes: string;
  paymentNotes: string;
  installmentSchedule: Array<{
    label: string;
    dueDate: string;
    amountCents: number;
    status: "Pending" | "Due Today" | "Paid" | "Missed" | "Scheduled";
  }>;
  activityFeed: Array<{
    id: BusinessCenterEntityId;
    kind: "Created" | "Updated" | "Reminder Sent" | "Letter Sent" | "Statement Sent" | "Payment Received" | "Broken" | "Resolved" | "Escalated";
    title: string;
    detail: string;
    createdAt: string;
  }>;
}

export interface ARRecoveryTicklerRecord {
  id: BusinessCenterEntityId;
  companyId: BusinessCenterEntityId;
  accountId: BusinessCenterEntityId;
  customerName: string;
  title: string;
  ticklerType:
    | "Callback"
    | "Escalation"
    | "Promise Follow-Up"
    | "Customer Touchpoint"
    | "Customer Call"
    | "Promise Verification"
    | "Payment Verification"
    | "Statement Follow-Up"
    | "Letter Follow-Up"
    | "Manager Review"
    | "Documentation Follow-Up"
    | "Research"
    | "Internal Follow-Up"
    | "Other"
    | "Custom";
  dueDate: string;
  status: "Open" | "Due Today" | "Upcoming" | "Overdue" | "Completed" | "Cancelled";
  priority: "Critical" | "High" | "Normal" | "Medium" | "Low";
  assignedCollector: string;
}

export interface ARRecoveryActivityRecord {
  id: BusinessCenterEntityId;
  companyId: BusinessCenterEntityId;
  accountId: BusinessCenterEntityId;
  customerName: string;
  kind: "Call" | "Email" | "Note" | "Statement" | "Letter" | "Promise" | "Escalation" | "Payment" | "Task";
  title: string;
  detail: string;
  createdAt: string;
}

export type RecoveryActivityEventType =
  | "note_added"
  | "follow_up_created"
  | "follow_up_completed"
  | "promise_created"
  | "promise_updated"
  | "promise_broken"
  | "escalation_created"
  | "escalation_approved"
  | "escalation_resolved"
  | "letter_generated"
  | "statement_generated"
  | "payment_recorded"
  | "task_created"
  | "task_completed"
  | "task_comment_added"
  | "collector_assigned"
  | "collector_reassigned"
  | "status_changed"
  | "document_uploaded"
  | "client_message_sent";

export interface RecoveryActivityEvent {
  id: BusinessCenterEntityId;
  companyId: BusinessCenterEntityId;
  accountId?: BusinessCenterEntityId;
  customerName?: string;
  actorId?: BusinessCenterEntityId;
  actorName?: string;
  actorRole?: "collector" | "manager" | "client" | "system";
  eventType: RecoveryActivityEventType;
  relatedRecordType?: "account" | "tickler" | "promise" | "letter" | "statement" | "escalation" | "payment" | "document" | "message" | "aging_import";
  relatedRecordId?: BusinessCenterEntityId;
  title: string;
  detail: string;
  notes?: string;
  outcome?: string;
  previousStatus?: string;
  nextStatus?: string;
  visibility: "internal" | "client_visible" | "manager_only";
  createdAt: string;
  metadata?: Record<string, unknown>;
}

export interface ARRecoveryCollectorRecord {
  id: BusinessCenterEntityId;
  companyId: BusinessCenterEntityId;
  collectorName: string;
  accountsAssigned: number;
  accountsWorked: number;
  callsCompleted: number;
  emailsSent: number;
  recoveryCents: number;
  promiseSuccessRate: number;
  averageResolutionDays: number;
}

export interface ARRecoveryClientProfile {
  businessName: string;
  dbaName: string;
  addressLine1: string;
  addressLine2?: string | null;
  phone: string;
  email: string;
  website: string;
  collectionDepartmentContact: string;
  accountingContact: string;
  escalationContact: string;
  communicationPreference: "Email Only" | "Phone Only" | "Email + Phone" | "Letter Required";
  letterDefaults: {
    businessName: string;
    address: string;
    phone: string;
    email: string;
    website: string;
    contactName: string;
  };
  portalUrl: string;
  emailSignature: string;
  letterhead: string;
  footerContent: string;
}

export interface ARRecoveryCollectionLetterRecord {
  id: BusinessCenterEntityId;
  companyId: BusinessCenterEntityId;
  accountId: BusinessCenterEntityId;
  customerName: string;
  customerNumber: string;
  clientCompany: string;
  template: "Friendly Reminder" | "Past Due Notice" | "Second Notice" | "Urgent Payment Request" | "Demand Letter" | "Final Notice" | "Promise Reminder" | "Broken Promise Letter" | "Settlement Offer" | "Account Review Notice" | "Custom Letter Builder";
  status: "Draft" | "Queued" | "Generated" | "Delivery Not Configured" | "Sent" | "Delivered" | "Viewed" | "Downloaded" | "Failed" | "Cancelled";
  sentAt: string | null;
  dateSent: string | null;
  timeSent: string | null;
  sentBy: string;
  deliveryStatus: "Pending" | "Sent" | "Delivered" | "Viewed" | "Downloaded" | "Failed";
  viewedStatus: "Pending" | "Viewed";
  downloadedStatus: "Pending" | "Downloaded";
  agingBucket: ARRecoveryCustomerAccountRecord["agingSummary"] | "Custom";
  balanceCents: number;
  collector: string;
  manager: string;
  notes: string;
  mergeFields: {
    businessName: string;
    dbaName: string;
    address: string;
    phone: string;
    email: string;
    website: string;
    contactName: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ARRecoveryStatementRecord {
  id: BusinessCenterEntityId;
  companyId: BusinessCenterEntityId;
  accountId: BusinessCenterEntityId;
  customerName: string;
  customerNumber: string;
  clientCompany: string;
  statementNumber: string;
  status: "Draft" | "Created" | "Queued" | "Generated" | "Delivery Not Configured" | "Sent" | "Delivered" | "Viewed" | "Opened" | "Downloaded" | "Failed" | "Cancelled";
  sentAt: string | null;
  dateSent: string | null;
  timeSent: string | null;
  sentBy: string;
  deliveryStatus: "Pending" | "Sent" | "Delivered" | "Opened" | "Downloaded" | "Failed";
  openedStatus: "Pending" | "Opened";
  downloadedStatus: "Pending" | "Downloaded";
  balanceCents: number;
  openInvoiceCount: number;
  dueDate: string;
  collector: string;
  manager: string;
  notes: string;
  lineItems: Array<{
    invoiceNumber: string;
    dueDate: string;
    amountCents: number;
    status: "Open" | "Past Due" | "Paid" | "Partial";
  }>;
  mergeFields: {
    businessName: string;
    dbaName: string;
    address: string;
    phone: string;
    email: string;
    website: string;
    contactName: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ARRecoveryWorkspace {
  company: BusinessCenterCompanyRecord;
  branding: ARRecoveryCompanyBranding;
  clientProfile: ARRecoveryClientProfile;
  accounts: ARRecoveryCustomerAccountRecord[];
  agingBuckets: ARRecoveryAgingBucketRecord[];
  promises: ARRecoveryPromiseRecord[];
  collectionLetters: ARRecoveryCollectionLetterRecord[];
  statements: ARRecoveryStatementRecord[];
  ticklers: ARRecoveryTicklerRecord[];
  activity: ARRecoveryActivityRecord[];
  recoveryActivityEvents: RecoveryActivityEvent[];
  collectors: ARRecoveryCollectorRecord[];
  newAccountsImported: number;
  collectionLettersPending: number;
  statementsPending: number;
}

export interface ARRecoveryDashboardSnapshot {
  company: BusinessCenterCompanyRecord;
  branding: ARRecoveryCompanyBranding;
  totalOpenAR: number;
  currentBalance: number;
  balance1to30: number;
  balance31to60: number;
  balance61to90: number;
  balance90Plus: number;
  recoveryDollars: number;
  recoveryRate: number;
  promiseToPayAmount: number;
  accountsAssigned: number;
  accountsRequiringAction: number;
  followUpsDueToday: number;
  overdueTicklers: number;
  brokenPromises: number;
  highRiskAccounts: number;
  escalationsNeeded: number;
  newAccountsImported: number;
  collectionLettersPending: number;
  statementsPending: number;
  ticklerTodayCount: number;
  ticklerOverdueCount: number;
  ticklerUpcomingCount: number;
  ticklerCriticalCount: number;
  ticklerHighCount: number;
  promisesDueTodayCount: number;
  promisesUpcomingCount: number;
  promisesBrokenCount: number;
  promisesPartialCount: number;
  promisesCompletedCount: number;
  companyScopeExample: {
    customerName: string;
    customerNumber: string;
    contactName: string;
    agingSummary: string;
    openBalance: number;
    assignedCollector: string;
    recoveryStatus: string;
    escalationStatus: string;
    notes: string;
  };
}

export type ARRecoveryLetterTabKey =
  | "overview"
  | "template"
  | "tracking"
  | "bulk"
  | "activity"
  | "account"
  | "history";

export interface ARRecoveryCollectionLetterCenterSnapshot {
  workspace: ARRecoveryWorkspace;
  search: string;
  filteredLetters: ARRecoveryCollectionLetterRecord[];
  selectedLetter: ARRecoveryCollectionLetterRecord | null;
  selectedTab: ARRecoveryLetterTabKey;
  dashboardCards: Array<{ label: string; value: string | number; note: string }>;
  templates: Array<{ label: ARRecoveryCollectionLetterRecord["template"]; count: number; note: string }>;
  statusCounts: Array<{ label: string; count: number; note: string }>;
  bulkActions: Array<{ label: string; description: string; priority: "Critical" | "High" | "Medium" | "Low" }>;
  mergePreview: Array<{ label: string; value: string }>;
  trackingMetrics: Array<{ label: string; value: string | number; note: string }>;
  activityFeed: Array<{
    id: BusinessCenterEntityId;
    title: string;
    detail: string;
    createdAt: string;
  }>;
  tabLabels: Array<{ key: ARRecoveryLetterTabKey; label: string; description: string }>;
}

export interface ARRecoveryStatementCenterSnapshot {
  workspace: ARRecoveryWorkspace;
  search: string;
  filteredStatements: ARRecoveryStatementRecord[];
  selectedStatement: ARRecoveryStatementRecord | null;
  selectedTab: ARRecoveryLetterTabKey;
  dashboardCards: Array<{ label: string; value: string | number; note: string }>;
  statusCounts: Array<{ label: string; count: number; note: string }>;
  bulkActions: Array<{ label: string; description: string; priority: "Critical" | "High" | "Medium" | "Low" }>;
  deliveryMetrics: Array<{ label: string; value: string | number; note: string }>;
  lineItemPreview: Array<{ invoiceNumber: string; dueDate: string; amountCents: number; status: string }>;
  activityFeed: Array<{
    id: BusinessCenterEntityId;
    title: string;
    detail: string;
    createdAt: string;
  }>;
  tabLabels: Array<{ key: ARRecoveryLetterTabKey; label: string; description: string }>;
}

export type ARRecoveryCommercializationTabKey =
  | "overview"
  | "onboarding"
  | "deployment"
  | "packages"
  | "success"
  | "support"
  | "documentation"
  | "training"
  | "automation"
  | "readiness";

export interface ARRecoveryCommercializationSnapshot {
  workspace: ARRecoveryWorkspace;
  selectedTab: ARRecoveryCommercializationTabKey;
  readinessScore: number;
  readinessCards: Array<{ label: string; value: string | number; note: string }>;
  launchSequence: Array<{ title: string; detail: string; steps: string[] }>;
  deploymentControls: Array<{ title: string; value: string; note: string }>;
  packageCatalog: Array<{ name: string; category: string; pricing: string; features: string[]; limits: string; activeClients: number; status: "Available" | "Active" }>;
  clientSuccessMetrics: Array<{ label: string; value: string | number; note: string }>;
  supportQueue: Array<{ title: string; detail: string; severity: "Critical" | "High" | "Medium" | "Low"; linkedTo: string }>;
  documentationLibrary: Array<{ title: string; category: string; description: string; status: string }>;
  trainingPrograms: Array<{ title: string; audience: string; completion: string; certification: string }>;
  automationAudit: Array<{ trigger: string; status: "Ready" | "Needs Review" | "Complete"; detail: string }>;
  launchChecklist: Array<{ label: string; completed: boolean; note: string }>;
  targetIndustries: Array<{ label: string; reason: string }>;
  tabLabels: Array<{ key: ARRecoveryCommercializationTabKey; label: string; description: string }>;
}

export type ARRecoveryPromiseTabKey =
  | "overview"
  | "account"
  | "notes"
  | "activity"
  | "payments"
  | "letters"
  | "statements"
  | "ticklers"
  | "documents"
  | "history";

export interface ARRecoveryPromiseCenterSnapshot {
  workspace: ARRecoveryWorkspace;
  search: string;
  filteredPromises: ARRecoveryPromiseRecord[];
  selectedPromise: ARRecoveryPromiseRecord | null;
  selectedTab: ARRecoveryPromiseTabKey;
  openPromisesCount: number;
  dueTodayCount: number;
  brokenPromisesCount: number;
  pastDuePromisesCount: number;
  promisesPaidCount: number;
  promisesKeptRate: number;
  promiseRecoveryCents: number;
  installmentPlansActiveCount: number;
  promiseTypeCounts: Array<{ label: ARRecoveryPromiseRecord["promiseType"]; count: number; note: string }>;
  dashboardCards: Array<{ label: string; value: string | number; note: string }>;
  collectorQueue: Array<{
    collectorName: string;
    openPromises: number;
    dueToday: number;
    broken: number;
    paid: number;
    installmentPlans: number;
    recoveryCents: number;
  }>;
  managerMetrics: Array<{ label: string; value: string | number; note: string }>;
  brokenPromiseSignals: Array<{ title: string; detail: string; severity: "Critical" | "High" | "Medium" }>;
  automationHooks: Array<{ title: string; detail: string }>;
  portalCompatibility: Array<{ title: string; detail: string }>;
  tabLabels: Array<{ key: ARRecoveryPromiseTabKey; label: string; description: string }>;
  activityFeed: Array<{
    id: BusinessCenterEntityId;
    customerName: string;
    title: string;
    detail: string;
    kind: string;
    createdAt: string;
  }>;
}

export type ARRecoveryAccountTabKey =
  | "overview"
  | "invoices"
  | "activity"
  | "notes"
  | "communications"
  | "tasks"
  | "letters"
  | "statements"
  | "promises"
  | "ticklers"
  | "documents"
  | "history";

export interface ARRecoveryCustomerAccountsSnapshot {
  workspace: ARRecoveryWorkspace;
  search: string;
  filteredAccounts: ARRecoveryCustomerAccountRecord[];
  selectedAccount: ARRecoveryCustomerAccountRecord | null;
  selectedTab: ARRecoveryAccountTabKey;
  totalOpenBalanceCents: number;
  riskAccountCount: number;
  actionReadyAccountCount: number;
  nextActionDate: string | null;
  tabLabels: Array<{ key: ARRecoveryAccountTabKey; label: string; description: string }>;
}

export interface ARRecoveryTicklerDashboardSnapshot {
  workspace: ARRecoveryWorkspace;
  totalTicklers: number;
  dueToday: ARRecoveryTicklerRecord[];
  overdue: ARRecoveryTicklerRecord[];
  upcoming: ARRecoveryTicklerRecord[];
  critical: ARRecoveryTicklerRecord[];
  assignedToMe: ARRecoveryTicklerRecord[];
  assignedToTeam: ARRecoveryTicklerRecord[];
  brokenPromiseFollowUps: ARRecoveryTicklerRecord[];
  highRiskFollowUps: ARRecoveryTicklerRecord[];
  collectorQueue: Array<{
    collectorName: string;
    dueAccounts: number;
    overdueAccounts: number;
    todayCalls: number;
    todayEmails: number;
    todayPromises: number;
    todayFollowUps: number;
    todayEscalations: number;
  }>;
  nextBestActions: Array<{
    label: string;
    description: string;
    priority: "Critical" | "High" | "Medium" | "Low";
  }>;
  automaticTriggers: Array<{ title: string; detail: string }>;
  managerMetrics: Array<{ label: string; value: string | number; note: string }>;
}

const AR_RECOVERY_TENANTS: Record<BusinessCenterEntityId, ARRecoveryWorkspace> = {
  truevance_connect: {
    company: {
      id: "truevance_connect",
      parentCompanyId: null,
      companyName: "CollabRecover AR Solutions",
      companySlug: "truevance-connect-recovery",
      companyType: "parent",
      brand: "truevance_connect",
      status: "active",
      createdAt: "2026-01-03T14:00:00.000Z",
      updatedAt: "2026-06-19T14:00:00.000Z",
    },
    branding: {
      displayName: "CollabRecover AR Solutions",
      headline: "AR recovery operations workspace",
      primaryColor: "#FF6A2B",
      accentColor: "#22E0D0",
      surfaceColor: "#07121F",
    },
    accounts: [
      {
        id: "ar-account-001",
        companyId: "truevance_connect",
        customerName: "Aster Media Group",
        customerNumber: "AR-1001",
        contactName: "Jordan Lee",
        contactEmail: "jordan@astermedia.com",
        contactPhone: "(555) 014-9981",
        agingSummary: "91+ days",
        openInvoiceCount: 4,
        openBalanceCents: 3245000,
        promiseToPayCount: 2,
        recoveryStatus: "Escalated",
        assignedCollector: "Maya Reynolds",
        escalationStatus: "Director Review",
        notes: "Large account with repeated broken promises and pending director follow-up.",
        createdAt: "2026-05-11T13:14:00.000Z",
        updatedAt: "2026-06-19T13:42:00.000Z",
      },
      {
        id: "ar-account-002",
        companyId: "truevance_connect",
        customerName: "Blue Harbor Logistics",
        customerNumber: "AR-1002",
        contactName: "Samir Patel",
        contactEmail: "billing@blueharbor.com",
        contactPhone: "(555) 011-3200",
        agingSummary: "31-60 days",
        openInvoiceCount: 3,
        openBalanceCents: 1788000,
        promiseToPayCount: 1,
        recoveryStatus: "At Risk",
        assignedCollector: "Maya Reynolds",
        escalationStatus: "Watch",
        notes: "Recent promise moved to follow-up queue after payment was missed.",
        createdAt: "2026-05-18T13:14:00.000Z",
        updatedAt: "2026-06-19T14:12:00.000Z",
      },
      {
        id: "ar-account-003",
        companyId: "truevance_connect",
        customerName: "Northstar Equipment",
        customerNumber: "AR-1003",
        contactName: "Elena Torres",
        contactEmail: "accounts@northstar.com",
        contactPhone: "(555) 017-4450",
        agingSummary: "1-30 days",
        openInvoiceCount: 2,
        openBalanceCents: 845000,
        promiseToPayCount: 1,
        recoveryStatus: "Payment Plan",
        assignedCollector: "Marcus Hill",
        escalationStatus: "None",
        notes: "Payment plan has been established and is being monitored via ticklers.",
        createdAt: "2026-06-02T13:14:00.000Z",
        updatedAt: "2026-06-19T13:56:00.000Z",
      },
      {
        id: "ar-account-004",
        companyId: "truevance_connect",
        customerName: "Crescent Interiors",
        customerNumber: "AR-1004",
        contactName: "Noelle Grant",
        contactEmail: "payables@crescentinteriors.com",
        contactPhone: "(555) 019-2800",
        agingSummary: "Current",
        openInvoiceCount: 1,
        openBalanceCents: 295000,
        promiseToPayCount: 0,
        recoveryStatus: "Current",
        assignedCollector: "Marcus Hill",
        escalationStatus: "None",
        notes: "Recently invoiced and not yet due, kept visible for early outreach.",
        createdAt: "2026-06-14T13:14:00.000Z",
        updatedAt: "2026-06-18T18:30:00.000Z",
      },
    ],
    agingBuckets: [
      { label: "Current", balanceCents: 240000, accountCount: 1, trendNote: "Fresh invoices are still within terms." },
      { label: "1-30 Days", balanceCents: 845000, accountCount: 2, trendNote: "Accounts entering the first follow-up window." },
      { label: "31-60 Days", balanceCents: 1788000, accountCount: 1, trendNote: "Requires collector action and reminder sequencing." },
      { label: "61-90 Days", balanceCents: 1385000, accountCount: 0, trendNote: "No accounts currently sitting in this bucket." },
      { label: "90+ Days", balanceCents: 3245000, accountCount: 1, trendNote: "Escalated account that needs command-center attention." },
    ],
    promises: [
      {
        id: "promise-001",
        companyId: "truevance_connect",
        accountId: "ar-account-001",
        customerName: "Aster Media Group",
        customerNumber: "AR-1001",
        clientCompany: "CollabRecover AR Solutions",
        invoiceLabels: ["INV-001", "INV-003", "INV-004"],
        remainingBalanceCents: 2045000,
        promiseDate: isoDate(0),
        amountCents: 1200000,
        status: "Due Today",
        promiseType: "Installment Plan",
        followUpDate: isoDate(1),
        collector: "Maya Reynolds",
        manager: "Danielle Carter",
        createdAt: timestampOffsetHours(-162),
        updatedAt: timestampOffsetHours(-6),
        notes: "Promise due today. Auto-escalate if no payment confirmation is logged by end of day.",
        collectorNotes: "Collector confirmed customer callback window for late afternoon.",
        customerNotes: "Customer expects to review internal remittance processing today.",
        managerNotes: "Manager review required if payment slips past EOD.",
        escalationNotes: "Director review remains active if payment is missed.",
        paymentNotes: "Awaiting remittance and partial settlement confirmation.",
        installmentSchedule: [
          { label: "Initial payment", dueDate: isoDate(0), amountCents: 550000, status: "Due Today" },
          { label: "Second installment", dueDate: isoDate(5), amountCents: 350000, status: "Scheduled" },
          { label: "Final installment", dueDate: isoDate(10), amountCents: 300000, status: "Scheduled" },
        ],
        activityFeed: [
          { id: "promise-act-001", kind: "Created", title: "Promise created", detail: "Installment promise opened for the open receivable.", createdAt: timestampOffsetHours(-162) },
          { id: "promise-act-002", kind: "Reminder Sent", title: "Reminder scheduled", detail: "Collector reminder queued for the promise date.", createdAt: timestampOffsetHours(-24) },
          { id: "promise-act-003", kind: "Updated", title: "Promise updated", detail: "Manager added escalation guardrails and follow-up timing.", createdAt: timestampOffsetHours(-6) },
        ],
      },
      {
        id: "promise-002",
        companyId: "truevance_connect",
        accountId: "ar-account-002",
        customerName: "Blue Harbor Logistics",
        customerNumber: "AR-1002",
        clientCompany: "CollabRecover AR Solutions",
        invoiceLabels: ["INV-002", "INV-005"],
        remainingBalanceCents: 1188000,
        promiseDate: isoDate(2),
        amountCents: 500000,
        status: "Pending",
        promiseType: "Settlement Agreement",
        followUpDate: isoDate(3),
        collector: "Maya Reynolds",
        manager: "Danielle Carter",
        createdAt: timestampOffsetHours(-126),
        updatedAt: timestampOffsetHours(-12),
        notes: "Follow-up scheduled before the promised date to reduce slippage.",
        collectorNotes: "Customer requested a settlement draft before approval.",
        customerNotes: "Finance lead wants terms reviewed internally first.",
        managerNotes: "Keep close to the due window to prevent slippage.",
        escalationNotes: "Escalate if approval stalls beyond the follow-up date.",
        paymentNotes: "Partial remittance may be needed before final approval.",
        installmentSchedule: [
          { label: "Settlement deposit", dueDate: isoDate(2), amountCents: 250000, status: "Pending" },
          { label: "Settlement balance", dueDate: isoDate(7), amountCents: 250000, status: "Scheduled" },
        ],
        activityFeed: [
          { id: "promise-act-004", kind: "Created", title: "Settlement promise created", detail: "Promise created after the last collector call.", createdAt: timestampOffsetHours(-126) },
          { id: "promise-act-005", kind: "Updated", title: "Follow-up scheduled", detail: "Collector scheduled a pre-due confirmation touchpoint.", createdAt: timestampOffsetHours(-12) },
        ],
      },
      {
        id: "promise-003",
        companyId: "truevance_connect",
        accountId: "ar-account-003",
        customerName: "Northstar Equipment",
        customerNumber: "AR-1003",
        clientCompany: "CollabRecover AR Solutions",
        invoiceLabels: ["INV-001"],
        remainingBalanceCents: 248000,
        promiseDate: isoDate(-2),
        amountCents: 300000,
        status: "Broken",
        promiseType: "Single Payment",
        followUpDate: isoDate(0),
        collector: "Marcus Hill",
        manager: "Danielle Carter",
        createdAt: timestampOffsetHours(-88),
        updatedAt: timestampOffsetHours(-5),
        notes: "Promise was missed and moved into the broken queue for escalation.",
        collectorNotes: "Customer missed the payment date and has not returned the callback.",
        customerNotes: "No response after payment reminder and previous callback.",
        managerNotes: "Prioritize escalation and manager intervention.",
        escalationNotes: "Broken promise should route directly to review.",
        paymentNotes: "No payment has been received against the commitment.",
        installmentSchedule: [
          { label: "Promise due", dueDate: isoDate(-2), amountCents: 300000, status: "Missed" },
        ],
        activityFeed: [
          { id: "promise-act-006", kind: "Created", title: "Promise created", detail: "Single payment commitment recorded.", createdAt: timestampOffsetHours(-88) },
          { id: "promise-act-007", kind: "Reminder Sent", title: "Reminder sent", detail: "Reminder sent before due date.", createdAt: timestampOffsetHours(-30) },
          { id: "promise-act-008", kind: "Broken", title: "Promise broken", detail: "The commitment elapsed without receipt.", createdAt: timestampOffsetHours(-5) },
        ],
      },
      {
        id: "promise-004",
        companyId: "truevance_connect",
        accountId: "ar-account-004",
        customerName: "Crescent Interiors",
        customerNumber: "AR-1004",
        clientCompany: "CollabRecover AR Solutions",
        invoiceLabels: ["INV-001"],
        remainingBalanceCents: 295000,
        promiseDate: isoDate(6),
        amountCents: 950000,
        status: "Partial",
        promiseType: "Partial Payment",
        followUpDate: isoDate(7),
        collector: "Marcus Hill",
        manager: "Danielle Carter",
        createdAt: timestampOffsetHours(-50),
        updatedAt: timestampOffsetHours(-2),
        notes: "Partial payment received; remainder still tracked in the follow-up queue.",
        collectorNotes: "Partial remittance arrived after the last collector email.",
        customerNotes: "Customer expects to settle the remainder next week.",
        managerNotes: "Monitor payment completion before moving to escalation.",
        escalationNotes: "If the remainder slips, activate the next reminder layer.",
        paymentNotes: "Partial payment captured and remaining balance is still open.",
        installmentSchedule: [
          { label: "Partial payment", dueDate: isoDate(0), amountCents: 650000, status: "Paid" },
          { label: "Remainder", dueDate: isoDate(6), amountCents: 300000, status: "Pending" },
        ],
        activityFeed: [
          { id: "promise-act-009", kind: "Created", title: "Partial promise created", detail: "Customer agreed to a partial payment arrangement.", createdAt: timestampOffsetHours(-50) },
          { id: "promise-act-010", kind: "Payment Received", title: "Partial payment received", detail: "Partial amount recorded against the promise.", createdAt: timestampOffsetHours(-2) },
        ],
      },
      {
        id: "promise-005",
        companyId: "truevance_connect",
        accountId: "ar-account-001",
        customerName: "Aster Media Group",
        customerNumber: "AR-1001",
        clientCompany: "CollabRecover AR Solutions",
        invoiceLabels: ["INV-006"],
        remainingBalanceCents: 0,
        promiseDate: isoDate(5),
        amountCents: 650000,
        status: "Paid",
        promiseType: "Single Payment",
        followUpDate: isoDate(8),
        collector: "Maya Reynolds",
        manager: "Danielle Carter",
        createdAt: timestampOffsetHours(-220),
        updatedAt: timestampOffsetHours(-10),
        notes: "Past promise was kept and closed out cleanly.",
        collectorNotes: "Promise closed after remittance was confirmed.",
        customerNotes: "Customer completed payment ahead of the follow-up call.",
        managerNotes: "Successful promise that can be reused as a reference pattern.",
        escalationNotes: "No escalation required.",
        paymentNotes: "Payment verified and matched to the expected amount.",
        installmentSchedule: [
          { label: "Final payment", dueDate: isoDate(5), amountCents: 650000, status: "Paid" },
        ],
        activityFeed: [
          { id: "promise-act-011", kind: "Created", title: "Promise created", detail: "Single payment promise recorded.", createdAt: timestampOffsetHours(-220) },
          { id: "promise-act-012", kind: "Payment Received", title: "Promise paid", detail: "Payment matched and promise was closed.", createdAt: timestampOffsetHours(-10) },
          { id: "promise-act-013", kind: "Resolved", title: "Promise resolved", detail: "Account is ready for archive review.", createdAt: timestampOffsetHours(-8) },
        ],
      },
    ],
    ticklers: [
      {
        id: "tickler-001",
        companyId: "truevance_connect",
        accountId: "ar-account-001",
        customerName: "Aster Media Group",
        title: "Director escalation callback",
        ticklerType: "Escalation",
        dueDate: isoDate(0),
        status: "Due Today",
        priority: "Critical",
        assignedCollector: "Maya Reynolds",
      },
      {
        id: "tickler-002",
        companyId: "truevance_connect",
        accountId: "ar-account-002",
        customerName: "Blue Harbor Logistics",
        title: "Promise-to-pay follow-up",
        ticklerType: "Promise Follow-Up",
        dueDate: isoDate(-1),
        status: "Overdue",
        priority: "High",
        assignedCollector: "Maya Reynolds",
      },
      {
        id: "tickler-003",
        companyId: "truevance_connect",
        accountId: "ar-account-003",
        customerName: "Northstar Equipment",
        title: "Customer payment-plan check-in",
        ticklerType: "Callback",
        dueDate: isoDate(2),
        status: "Upcoming",
        priority: "Medium",
        assignedCollector: "Marcus Hill",
      },
      {
        id: "tickler-004",
        companyId: "truevance_connect",
        accountId: "ar-account-004",
        customerName: "Crescent Interiors",
        title: "Statement resend",
        ticklerType: "Customer Touchpoint",
        dueDate: isoDate(4),
        status: "Upcoming",
        priority: "Low",
        assignedCollector: "Marcus Hill",
      },
      {
        id: "tickler-005",
        companyId: "truevance_connect",
        accountId: "ar-account-001",
        customerName: "Aster Media Group",
        title: "Escalation review note",
        ticklerType: "Custom",
        dueDate: isoDate(-3),
        status: "Overdue",
        priority: "Critical",
        assignedCollector: "Maya Reynolds",
      },
    ],
    activity: [
      {
        id: "activity-001",
        companyId: "truevance_connect",
        accountId: "ar-account-001",
        customerName: "Aster Media Group",
        kind: "Call",
        title: "Collector call completed",
        detail: "Reviewed settlement options and confirmed director review was needed.",
        createdAt: timestampOffsetHours(-12),
      },
      {
        id: "activity-002",
        companyId: "truevance_connect",
        accountId: "ar-account-002",
        customerName: "Blue Harbor Logistics",
        kind: "Email",
        title: "Promise reminder emailed",
        detail: "Reminder sent ahead of the next follow-up checkpoint.",
        createdAt: timestampOffsetHours(-43),
      },
      {
        id: "activity-003",
        companyId: "truevance_connect",
        accountId: "ar-account-003",
        customerName: "Northstar Equipment",
        kind: "Promise",
        title: "Promise marked broken",
        detail: "The promise-to-pay date passed without confirmation.",
        createdAt: timestampOffsetHours(-53),
      },
      {
        id: "activity-004",
        companyId: "truevance_connect",
        accountId: "ar-account-004",
        customerName: "Crescent Interiors",
        kind: "Statement",
        title: "Monthly statement sent",
        detail: "Statement was delivered and tracked for view confirmation.",
        createdAt: timestampOffsetHours(-74),
      },
      {
        id: "activity-005",
        companyId: "truevance_connect",
        accountId: "ar-account-001",
        customerName: "Aster Media Group",
        kind: "Escalation",
        title: "Escalation queue updated",
        detail: "Account was elevated to director review for immediate attention.",
        createdAt: timestampOffsetHours(-103),
      },
      {
        id: "activity-006",
        companyId: "truevance_connect",
        accountId: "ar-account-002",
        customerName: "Blue Harbor Logistics",
        kind: "Note",
        title: "Collector note added",
        detail: "Customer requested a revised callback window and shorter follow-up cadence.",
        createdAt: timestampOffsetHours(-130),
      },
    ],
    recoveryActivityEvents: [
      {
        id: "recovery-event-001",
        companyId: "truevance_connect",
        accountId: "ar-account-001",
        customerName: "Aster Media Group",
        actorId: "collector-001",
        actorName: "Maya Reynolds",
        actorRole: "collector",
        eventType: "collector_assigned",
        relatedRecordType: "account",
        relatedRecordId: "ar-account-001",
        title: "Collector assigned",
        detail: "Maya Reynolds was assigned to the Aster Media Group recovery account.",
        notes: "High-balance account assigned to the senior collector queue.",
        visibility: "internal",
        createdAt: timestampOffsetHours(-240),
      },
      {
        id: "recovery-event-002",
        companyId: "truevance_connect",
        accountId: "ar-account-001",
        customerName: "Aster Media Group",
        actorId: "collector-001",
        actorName: "Maya Reynolds",
        actorRole: "collector",
        eventType: "promise_created",
        relatedRecordType: "promise",
        relatedRecordId: "promise-001",
        title: "Installment promise created",
        detail: "Customer agreed to a structured payment promise tied to three open invoices.",
        notes: "Promise requires same-day confirmation and manager visibility.",
        visibility: "internal",
        createdAt: timestampOffsetHours(-162),
        metadata: { amountCents: 1200000, invoiceLabels: ["INV-001", "INV-003", "INV-004"] },
      },
      {
        id: "recovery-event-003",
        companyId: "truevance_connect",
        accountId: "ar-account-001",
        customerName: "Aster Media Group",
        actorId: "collector-001",
        actorName: "Maya Reynolds",
        actorRole: "collector",
        eventType: "follow_up_created",
        relatedRecordType: "tickler",
        relatedRecordId: "tickler-001",
        title: "Director escalation callback scheduled",
        detail: "A critical follow-up was created to confirm the director review window.",
        notes: "Collector should verify remittance status before escalation decision.",
        visibility: "internal",
        createdAt: timestampOffsetHours(-28),
        metadata: { nextFollowUpDate: isoDate(0), nextAction: "Call customer and confirm payment timing." },
      },
      {
        id: "recovery-event-004",
        companyId: "truevance_connect",
        accountId: "ar-account-001",
        customerName: "Aster Media Group",
        actorId: "collector-001",
        actorName: "Maya Reynolds",
        actorRole: "collector",
        eventType: "note_added",
        relatedRecordType: "tickler",
        relatedRecordId: "tickler-001",
        title: "Tickler note added",
        detail: "Customer expects to review internal remittance processing before the callback.",
        notes: "Ask for payment confirmation number or revised promise before close of business.",
        visibility: "internal",
        createdAt: timestampOffsetHours(-18),
        metadata: { nextAction: "Request remittance confirmation." },
      },
      {
        id: "recovery-event-005",
        companyId: "truevance_connect",
        accountId: "ar-account-001",
        customerName: "Aster Media Group",
        actorId: "collector-001",
        actorName: "Maya Reynolds",
        actorRole: "collector",
        eventType: "follow_up_completed",
        relatedRecordType: "tickler",
        relatedRecordId: "tickler-005",
        title: "Escalation review note completed",
        detail: "Previous escalation follow-up was completed and moved into director review.",
        outcome: "Director review remains active until payment is confirmed.",
        previousStatus: "Overdue",
        nextStatus: "Completed",
        visibility: "manager_only",
        createdAt: timestampOffsetHours(-12),
        metadata: { nextAction: "Manager review before additional letter escalation." },
      },
      {
        id: "recovery-event-006",
        companyId: "truevance_connect",
        accountId: "ar-account-001",
        customerName: "Aster Media Group",
        actorId: "manager-001",
        actorName: "Danielle Carter",
        actorRole: "manager",
        eventType: "escalation_created",
        relatedRecordType: "escalation",
        relatedRecordId: "escalation-001",
        title: "Director review escalation opened",
        detail: "Account was elevated because the balance, aging, and promise risk all crossed review thresholds.",
        notes: "Manager should approve next outreach step after callback results are logged.",
        visibility: "manager_only",
        createdAt: timestampOffsetHours(-10),
      },
      {
        id: "recovery-event-007",
        companyId: "truevance_connect",
        accountId: "ar-account-001",
        customerName: "Aster Media Group",
        actorId: "collector-001",
        actorName: "Maya Reynolds",
        actorRole: "collector",
        eventType: "letter_generated",
        relatedRecordType: "letter",
        relatedRecordId: "letter-001",
        title: "Urgent payment request generated",
        detail: "A client-branded urgent payment letter was generated for the account.",
        notes: "Hold send until manager review if payment confirmation arrives today.",
        visibility: "client_visible",
        createdAt: timestampOffsetHours(-5),
      },
      {
        id: "recovery-event-008",
        companyId: "truevance_connect",
        accountId: "ar-account-002",
        customerName: "Blue Harbor Logistics",
        actorId: "collector-001",
        actorName: "Maya Reynolds",
        actorRole: "collector",
        eventType: "follow_up_completed",
        relatedRecordType: "tickler",
        relatedRecordId: "tickler-002",
        title: "Promise follow-up completed",
        detail: "Collector completed the overdue promise follow-up and requested revised settlement timing.",
        notes: "Customer requested a revised callback window and shorter follow-up cadence.",
        outcome: "Customer needs settlement draft before approval.",
        previousStatus: "Overdue",
        nextStatus: "Completed",
        visibility: "internal",
        createdAt: timestampOffsetHours(-4),
        metadata: { nextAction: "Send settlement draft and create next follow-up.", nextFollowUpDate: isoDate(1) },
      },
      {
        id: "recovery-event-009",
        companyId: "truevance_connect",
        accountId: "ar-account-002",
        customerName: "Blue Harbor Logistics",
        actorId: "collector-001",
        actorName: "Maya Reynolds",
        actorRole: "collector",
        eventType: "promise_updated",
        relatedRecordType: "promise",
        relatedRecordId: "promise-002",
        title: "Settlement promise updated",
        detail: "Follow-up was moved earlier to reduce promise slippage.",
        notes: "Keep close to the due window to prevent another missed commitment.",
        previousStatus: "Open",
        nextStatus: "Pending",
        visibility: "internal",
        createdAt: timestampOffsetHours(-12),
      },
      {
        id: "recovery-event-010",
        companyId: "truevance_connect",
        accountId: "ar-account-003",
        customerName: "Northstar Equipment",
        actorId: "collector-002",
        actorName: "Marcus Hill",
        actorRole: "collector",
        eventType: "promise_broken",
        relatedRecordType: "promise",
        relatedRecordId: "promise-003",
        title: "Promise marked broken",
        detail: "The promise-to-pay date elapsed without payment confirmation.",
        notes: "No response after payment reminder and previous callback.",
        outcome: "Move into broken promise review.",
        previousStatus: "Open",
        nextStatus: "Broken",
        visibility: "internal",
        createdAt: timestampOffsetHours(-5),
      },
      {
        id: "recovery-event-011",
        companyId: "truevance_connect",
        accountId: "ar-account-004",
        customerName: "Crescent Interiors",
        actorId: "system-001",
        actorName: "Recovery OS",
        actorRole: "system",
        eventType: "statement_generated",
        relatedRecordType: "statement",
        relatedRecordId: "statement-001",
        title: "Monthly statement generated",
        detail: "A customer statement was generated and sent for current balance visibility.",
        visibility: "client_visible",
        createdAt: timestampOffsetHours(-74),
      },
      {
        id: "recovery-event-012",
        companyId: "truevance_connect",
        accountId: "ar-account-004",
        customerName: "Crescent Interiors",
        actorId: "collector-002",
        actorName: "Marcus Hill",
        actorRole: "collector",
        eventType: "payment_recorded",
        relatedRecordType: "payment",
        relatedRecordId: "payment-001",
        title: "Partial payment recorded",
        detail: "Partial remittance was recorded against the active promise.",
        outcome: "Remaining balance stays in follow-up queue.",
        visibility: "client_visible",
        createdAt: timestampOffsetHours(-2),
        metadata: { amountCents: 650000 },
      },
      {
        id: "recovery-event-013",
        companyId: "truevance_connect",
        accountId: "ar-account-001",
        customerName: "Aster Media Group",
        actorId: "manager-001",
        actorName: "Danielle Carter",
        actorRole: "manager",
        eventType: "note_added",
        relatedRecordType: "account",
        relatedRecordId: "ar-account-001",
        title: "Manager note added",
        detail: "Director review should stay active until the same-day callback is complete.",
        notes: "Do not close escalation until payment proof is attached or the promise is revised.",
        visibility: "manager_only",
        createdAt: timestampOffsetHours(-3),
      },
      {
        id: "recovery-event-014",
        companyId: "truevance_connect",
        accountId: "ar-account-002",
        customerName: "Blue Harbor Logistics",
        actorId: "collector-001",
        actorName: "Maya Reynolds",
        actorRole: "collector",
        eventType: "status_changed",
        relatedRecordType: "account",
        relatedRecordId: "ar-account-002",
        title: "Account status changed",
        detail: "Account was moved into At Risk after the latest missed payment window.",
        previousStatus: "Current",
        nextStatus: "At Risk",
        visibility: "internal",
        createdAt: timestampOffsetHours(-43),
      },
      {
        id: "recovery-event-015",
        companyId: "truevance_connect",
        accountId: "ar-account-003",
        customerName: "Northstar Equipment",
        actorId: "collector-002",
        actorName: "Marcus Hill",
        actorRole: "collector",
        eventType: "follow_up_created",
        relatedRecordType: "tickler",
        relatedRecordId: "tickler-003",
        title: "Payment-plan check-in created",
        detail: "Upcoming callback was created to confirm the payment plan remains on track.",
        notes: "Confirm remaining installment timing and ask whether the customer needs a fresh statement.",
        visibility: "internal",
        createdAt: timestampOffsetHours(-20),
        metadata: { nextAction: "Call customer before payment-plan checkpoint.", nextFollowUpDate: isoDate(2) },
      },
    ],
    collectors: [
      {
        id: "collector-001",
        companyId: "truevance_connect",
        collectorName: "Maya Reynolds",
        accountsAssigned: 18,
        accountsWorked: 16,
        callsCompleted: 31,
        emailsSent: 54,
        recoveryCents: 1825000,
        promiseSuccessRate: 78,
        averageResolutionDays: 14,
      },
      {
        id: "collector-002",
        companyId: "truevance_connect",
        collectorName: "Marcus Hill",
        accountsAssigned: 14,
        accountsWorked: 12,
        callsCompleted: 22,
        emailsSent: 36,
        recoveryCents: 940000,
        promiseSuccessRate: 71,
        averageResolutionDays: 18,
      },
    ],
    clientProfile: {
      businessName: "CollabRecover AR Solutions",
      dbaName: "CollabRecover AR Solutions",
      addressLine1: "4200 Meridian Parkway",
      addressLine2: "Suite 250",
      phone: "(555) 210-9988",
      email: "collections@collabrecover.com",
      website: "https://collabrecover.com",
      collectionDepartmentContact: "Danielle Carter",
      accountingContact: "Finance Desk",
      escalationContact: "Director Review Desk",
      communicationPreference: "Email + Phone",
      letterDefaults: {
        businessName: "CollabRecover AR Solutions",
        address: "4200 Meridian Parkway, Suite 250, Dallas, TX 75201",
        phone: "(555) 210-9988",
        email: "collections@collabrecover.com",
        website: "https://collabrecover.com",
        contactName: "Danielle Carter",
      },
      portalUrl: "https://portal.collabrecover.com",
      emailSignature: "CollabRecover AR Solutions | collections@collabrecover.com | (555) 210-9988",
      letterhead: "CollabRecover AR Solutions letterhead",
      footerContent: "Confidential recovery correspondence prepared for business operations.",
    },
    collectionLetters: [
      {
        id: "letter-001",
        companyId: "truevance_connect",
        accountId: "ar-account-001",
        customerName: "Aster Media Group",
        customerNumber: "AR-1001",
        clientCompany: "CollabRecover AR Solutions",
        template: "Broken Promise Letter",
        status: "Sent",
        sentAt: timestampOffsetHours(-5),
        dateSent: isoDate(0),
        timeSent: "08:15",
        sentBy: "Maya Reynolds",
        deliveryStatus: "Delivered",
        viewedStatus: "Viewed",
        downloadedStatus: "Pending",
        agingBucket: "91+ days",
        balanceCents: 3245000,
        collector: "Maya Reynolds",
        manager: "Danielle Carter",
        notes: "Broken promise letter sent after the missed commitment was confirmed.",
        mergeFields: {
          businessName: "CollabRecover AR Solutions",
          dbaName: "CollabRecover AR Solutions",
          address: "4200 Meridian Parkway, Suite 250, Dallas, TX 75201",
          phone: "(555) 210-9988",
          email: "collections@collabrecover.com",
          website: "https://collabrecover.com",
          contactName: "Danielle Carter",
        },
        createdAt: timestampOffsetHours(-28),
        updatedAt: timestampOffsetHours(-5),
      },
      {
        id: "letter-002",
        companyId: "truevance_connect",
        accountId: "ar-account-002",
        customerName: "Blue Harbor Logistics",
        customerNumber: "AR-1002",
        clientCompany: "CollabRecover AR Solutions",
        template: "Friendly Reminder",
        status: "Delivered",
        sentAt: timestampOffsetHours(-18),
        dateSent: isoDate(0),
        timeSent: "10:45",
        sentBy: "Maya Reynolds",
        deliveryStatus: "Delivered",
        viewedStatus: "Pending",
        downloadedStatus: "Pending",
        agingBucket: "31-60 days",
        balanceCents: 1788000,
        collector: "Maya Reynolds",
        manager: "Danielle Carter",
        notes: "Friendly reminder letter issued before the next follow-up window.",
        mergeFields: {
          businessName: "CollabRecover AR Solutions",
          dbaName: "CollabRecover AR Solutions",
          address: "4200 Meridian Parkway, Suite 250, Dallas, TX 75201",
          phone: "(555) 210-9988",
          email: "collections@collabrecover.com",
          website: "https://collabrecover.com",
          contactName: "Danielle Carter",
        },
        createdAt: timestampOffsetHours(-34),
        updatedAt: timestampOffsetHours(-18),
      },
      {
        id: "letter-003",
        companyId: "truevance_connect",
        accountId: "ar-account-003",
        customerName: "Northstar Equipment",
        customerNumber: "AR-1003",
        clientCompany: "CollabRecover AR Solutions",
        template: "Promise Reminder",
        status: "Queued",
        sentAt: null,
        dateSent: null,
        timeSent: null,
        sentBy: "Marcus Hill",
        deliveryStatus: "Pending",
        viewedStatus: "Pending",
        downloadedStatus: "Pending",
        agingBucket: "1-30 days",
        balanceCents: 845000,
        collector: "Marcus Hill",
        manager: "Danielle Carter",
        notes: "Queued to reinforce the current payment plan commitment.",
        mergeFields: {
          businessName: "CollabRecover AR Solutions",
          dbaName: "CollabRecover AR Solutions",
          address: "4200 Meridian Parkway, Suite 250, Dallas, TX 75201",
          phone: "(555) 210-9988",
          email: "collections@collabrecover.com",
          website: "https://collabrecover.com",
          contactName: "Danielle Carter",
        },
        createdAt: timestampOffsetHours(-12),
        updatedAt: timestampOffsetHours(-12),
      },
      {
        id: "letter-004",
        companyId: "truevance_connect",
        accountId: "ar-account-004",
        customerName: "Crescent Interiors",
        customerNumber: "AR-1004",
        clientCompany: "CollabRecover AR Solutions",
        template: "Past Due Notice",
        status: "Draft",
        sentAt: null,
        dateSent: null,
        timeSent: null,
        sentBy: "Marcus Hill",
        deliveryStatus: "Pending",
        viewedStatus: "Pending",
        downloadedStatus: "Pending",
        agingBucket: "Current",
        balanceCents: 295000,
        collector: "Marcus Hill",
        manager: "Danielle Carter",
        notes: "Draft prepared for an early-stage reminder if aging moves.",
        mergeFields: {
          businessName: "CollabRecover AR Solutions",
          dbaName: "CollabRecover AR Solutions",
          address: "4200 Meridian Parkway, Suite 250, Dallas, TX 75201",
          phone: "(555) 210-9988",
          email: "collections@collabrecover.com",
          website: "https://collabrecover.com",
          contactName: "Danielle Carter",
        },
        createdAt: timestampOffsetHours(-3),
        updatedAt: timestampOffsetHours(-3),
      },
    ],
    statements: [
      {
        id: "statement-001",
        companyId: "truevance_connect",
        accountId: "ar-account-001",
        customerName: "Aster Media Group",
        customerNumber: "AR-1001",
        clientCompany: "CollabRecover AR Solutions",
        statementNumber: "ST-1001",
        status: "Sent",
        sentAt: timestampOffsetHours(-7),
        dateSent: isoDate(0),
        timeSent: "07:30",
        sentBy: "Maya Reynolds",
        deliveryStatus: "Delivered",
        openedStatus: "Opened",
        downloadedStatus: "Downloaded",
        balanceCents: 3245000,
        openInvoiceCount: 4,
        dueDate: isoDate(0),
        collector: "Maya Reynolds",
        manager: "Danielle Carter",
        notes: "Statement sent with the latest aging and balance summary.",
        lineItems: [
          { invoiceNumber: "INV-001", dueDate: isoDate(-14), amountCents: 1200000, status: "Past Due" },
          { invoiceNumber: "INV-003", dueDate: isoDate(-7), amountCents: 975000, status: "Past Due" },
          { invoiceNumber: "INV-004", dueDate: isoDate(-1), amountCents: 1070000, status: "Open" },
        ],
        mergeFields: {
          businessName: "CollabRecover AR Solutions",
          dbaName: "CollabRecover AR Solutions",
          address: "4200 Meridian Parkway, Suite 250, Dallas, TX 75201",
          phone: "(555) 210-9988",
          email: "collections@collabrecover.com",
          website: "https://collabrecover.com",
          contactName: "Danielle Carter",
        },
        createdAt: timestampOffsetHours(-22),
        updatedAt: timestampOffsetHours(-7),
      },
      {
        id: "statement-002",
        companyId: "truevance_connect",
        accountId: "ar-account-002",
        customerName: "Blue Harbor Logistics",
        customerNumber: "AR-1002",
        clientCompany: "CollabRecover AR Solutions",
        statementNumber: "ST-1002",
        status: "Delivered",
        sentAt: timestampOffsetHours(-20),
        dateSent: isoDate(0),
        timeSent: "11:10",
        sentBy: "Maya Reynolds",
        deliveryStatus: "Delivered",
        openedStatus: "Pending",
        downloadedStatus: "Pending",
        balanceCents: 1788000,
        openInvoiceCount: 3,
        dueDate: isoDate(2),
        collector: "Maya Reynolds",
        manager: "Danielle Carter",
        notes: "Delivered statement supporting the settlement agreement review.",
        lineItems: [
          { invoiceNumber: "INV-002", dueDate: isoDate(-9), amountCents: 720000, status: "Past Due" },
          { invoiceNumber: "INV-005", dueDate: isoDate(-2), amountCents: 580000, status: "Open" },
          { invoiceNumber: "INV-008", dueDate: isoDate(2), amountCents: 488000, status: "Open" },
        ],
        mergeFields: {
          businessName: "CollabRecover AR Solutions",
          dbaName: "CollabRecover AR Solutions",
          address: "4200 Meridian Parkway, Suite 250, Dallas, TX 75201",
          phone: "(555) 210-9988",
          email: "collections@collabrecover.com",
          website: "https://collabrecover.com",
          contactName: "Danielle Carter",
        },
        createdAt: timestampOffsetHours(-30),
        updatedAt: timestampOffsetHours(-20),
      },
      {
        id: "statement-003",
        companyId: "truevance_connect",
        accountId: "ar-account-003",
        customerName: "Northstar Equipment",
        customerNumber: "AR-1003",
        clientCompany: "CollabRecover AR Solutions",
        statementNumber: "ST-1003",
        status: "Queued",
        sentAt: null,
        dateSent: null,
        timeSent: null,
        sentBy: "Marcus Hill",
        deliveryStatus: "Pending",
        openedStatus: "Pending",
        downloadedStatus: "Pending",
        balanceCents: 845000,
        openInvoiceCount: 2,
        dueDate: isoDate(1),
        collector: "Marcus Hill",
        manager: "Danielle Carter",
        notes: "Queued as part of the payment-plan follow-up sequence.",
        lineItems: [
          { invoiceNumber: "INV-001", dueDate: isoDate(-5), amountCents: 445000, status: "Partial" },
          { invoiceNumber: "INV-007", dueDate: isoDate(1), amountCents: 400000, status: "Open" },
        ],
        mergeFields: {
          businessName: "CollabRecover AR Solutions",
          dbaName: "CollabRecover AR Solutions",
          address: "4200 Meridian Parkway, Suite 250, Dallas, TX 75201",
          phone: "(555) 210-9988",
          email: "collections@collabrecover.com",
          website: "https://collabrecover.com",
          contactName: "Danielle Carter",
        },
        createdAt: timestampOffsetHours(-8),
        updatedAt: timestampOffsetHours(-8),
      },
      {
        id: "statement-004",
        companyId: "truevance_connect",
        accountId: "ar-account-004",
        customerName: "Crescent Interiors",
        customerNumber: "AR-1004",
        clientCompany: "CollabRecover AR Solutions",
        statementNumber: "ST-1004",
        status: "Created",
        sentAt: null,
        dateSent: null,
        timeSent: null,
        sentBy: "Marcus Hill",
        deliveryStatus: "Pending",
        openedStatus: "Pending",
        downloadedStatus: "Pending",
        balanceCents: 295000,
        openInvoiceCount: 1,
        dueDate: isoDate(4),
        collector: "Marcus Hill",
        manager: "Danielle Carter",
        notes: "Prepared for a next-cycle statement if the account shifts out of current status.",
        lineItems: [
          { invoiceNumber: "INV-009", dueDate: isoDate(4), amountCents: 295000, status: "Open" },
        ],
        mergeFields: {
          businessName: "CollabRecover AR Solutions",
          dbaName: "CollabRecover AR Solutions",
          address: "4200 Meridian Parkway, Suite 250, Dallas, TX 75201",
          phone: "(555) 210-9988",
          email: "collections@collabrecover.com",
          website: "https://collabrecover.com",
          contactName: "Danielle Carter",
        },
        createdAt: timestampOffsetHours(-2),
        updatedAt: timestampOffsetHours(-2),
      },
    ],
    newAccountsImported: 9,
    collectionLettersPending: 6,
    statementsPending: 4,
  },
  kreatethedate: {
    company: {
      id: "kreatethedate",
      parentCompanyId: null,
      companyName: "CollabRecover AR Solutions",
      companySlug: "kreatethedate-recovery",
      companyType: "parent",
      brand: "kreatethedate",
      status: "active",
      createdAt: "2026-01-03T14:00:00.000Z",
      updatedAt: "2026-06-19T14:00:00.000Z",
    },
    branding: {
      displayName: "CollabRecover AR Solutions",
      headline: "White-label AR recovery workspace for another tenant",
      primaryColor: "#FF6B35",
      accentColor: "#22E0D0",
      surfaceColor: "#101826",
    },
    clientProfile: {
      businessName: "CollabRecover AR Solutions",
      dbaName: "CollabRecover AR Solutions",
      addressLine1: "100 Event Lane",
      addressLine2: null,
      phone: "(555) 222-1200",
      email: "collections@collabrecover.com",
      website: "https://collabrecover.com",
      collectionDepartmentContact: "Operations Desk",
      accountingContact: "Accounting Desk",
      escalationContact: "Leadership Desk",
      communicationPreference: "Email + Phone",
      letterDefaults: {
        businessName: "CollabRecover AR Solutions",
        address: "100 Event Lane, Nashville, TN 37201",
        phone: "(555) 222-1200",
        email: "collections@collabrecover.com",
        website: "https://collabrecover.com",
        contactName: "Operations Desk",
      },
      portalUrl: "https://portal.collabrecover.com",
      emailSignature: "CollabRecover AR Solutions | collections@collabrecover.com | (555) 222-1200",
      letterhead: "CollabRecover AR Solutions letterhead",
      footerContent: "Confidential event recovery correspondence.",
    },
    accounts: [],
    agingBuckets: [],
    promises: [],
    collectionLetters: [],
    statements: [],
    ticklers: [],
    activity: [],
    recoveryActivityEvents: [],
    collectors: [],
    newAccountsImported: 0,
    collectionLettersPending: 0,
    statementsPending: 0,
  },
};

const ACTIVE_AR_RECOVERY_COMPANY_ID = "truevance_connect";

const RECOVERY_NAVIGATION: ARRecoverySectionLink[] = [
  { key: "dashboard", label: "Recovery Dashboard", href: "/admin/ar-recovery", description: "Command center overview and daily action queue.", state: "Active" },
  { key: "client-management", label: "Client Management", href: "/admin/ar-recovery/client-management", description: "Paying-client profiles, contacts, assignments, lifecycle, and portfolio readiness.", state: "Active" },
  { key: "client-exchange", label: "Client Exchange Center", href: "/admin/ar-recovery/client-exchange", description: "Portfolio intake, validation, mapping, approval, import, assignment, and activation.", state: "Ready" },
  { key: "customer-account-management", label: "Customers & Accounts", href: "/admin/ar-recovery/customer-account-management", description: "Client-bound customer and account master records.", state: "Active" },
  { key: "customer-accounts", label: "Account Recovery", href: "/admin/ar-recovery/customer-accounts", description: "Focused single-account recovery workspace for communications, promises, documents, payments, and recovery actions.", state: "Ready" },
  { key: "aging-management", label: "Aging Management Center", href: "/admin/ar-recovery/aging-management", description: "Aging buckets, uploads, filtering, and prioritization.", state: "Ready" },
  { key: "promise-to-pay", label: "Promise-To-Pay Center", href: "/admin/ar-recovery/promise-to-pay", description: "Promise tracking, status changes, and follow-ups.", state: "Ready" },
  { key: "smart-tickler", label: "Smart Tickler Center", href: "/admin/ar-recovery/smart-tickler", description: "Follow-up queue, reminders, and escalation timing.", state: "Ready" },
  { key: "collection-letters", label: "Collection Letter Center", href: "/admin/ar-recovery/collection-letters", description: "Letter templates, send workflows, and tracking.", state: "Ready" },
  { key: "document-center", label: "Document Center", href: "/admin/ar-recovery/document-center", description: "Professional letters, statements, templates, and document history.", state: "Ready" },
  { key: "statements", label: "Statement Center", href: "/admin/ar-recovery/statements", description: "Statement layouts, delivery tracking, and history.", state: "Ready" },
  { key: "payment-center", label: "Payment Center", href: "/admin/ar-recovery/payments", description: "Canonical payment intake, matching, allocation, verification, and audit.", state: "Ready" },
  { key: "settlement-center", label: "Settlement Center", href: "/admin/ar-recovery/settlements", description: "Recovery-fee eligibility, settlement drafts, approval, client invoice drafts, and audit.", state: "Ready" },
  { key: "billing-center", label: "Billing Center", href: "/admin/ar-recovery/billing", description: "Client billing invoices, hosted Pay Now, receipts, reminders, and billing history.", state: "Ready" },
  { key: "recovery-activity", label: "Recovery Activity Timeline", href: "/admin/ar-recovery/recovery-activity", description: "Chronological notes, calls, statements, and payment updates.", state: "Ready" },
  { key: "recovery-reporting", label: "Recovery Reporting", href: "/admin/ar-recovery/recovery-reporting", description: "Collector performance and recovery reporting foundation.", state: "Ready" },
  { key: "recovery-analytics", label: "Recovery Analytics", href: "/admin/ar-recovery/recovery-analytics", description: "Advanced charts and trend surfaces.", state: "Ready" },
  { key: "manager-command-center", label: "Manager Command Center", href: "/admin/ar-recovery/manager-command-center", description: "Team oversight, collector workload, and escalation management.", state: "Ready" },
  { key: "delivery-operations", label: "Delivery Operations", href: "/admin/ar-recovery/delivery-operations", description: "Governed delivery exception review, assignment, and evidence-led outcomes.", state: "Active" },
  { key: "client-portal", label: "Client Portal Foundation", href: "/admin/ar-recovery/client-portal", description: "Client portal document navigation and company-scoped visibility.", state: "Ready" },
  { key: "white-label-settings", label: "Administration", href: "/admin/ar-recovery/white-label-settings", description: "Users, roles, permissions, teams, and platform service foundations.", state: "Planned" },
  { key: "commercialization-center", label: "Business Platform", href: "/admin/ar-recovery/commercialization-center", description: "Onboarding, packaging, support, training, and deployment readiness.", state: "Ready" },
];

export function loadARRecoveryWorkspace(companyId: BusinessCenterEntityId = ACTIVE_AR_RECOVERY_COMPANY_ID): ARRecoveryWorkspace {
  return AR_RECOVERY_TENANTS[companyId] || AR_RECOVERY_TENANTS[ACTIVE_AR_RECOVERY_COMPANY_ID];
}

export function getRecoveryActivityEvents(workspace: ARRecoveryWorkspace) {
  return [...workspace.recoveryActivityEvents].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

export function filterRecoveryActivityEventsByAccount(events: RecoveryActivityEvent[], accountId: BusinessCenterEntityId) {
  return events.filter((event) => event.accountId === accountId || (event.relatedRecordType === "account" && event.relatedRecordId === accountId));
}

export function filterRecoveryActivityEventsByRelatedRecord(
  events: RecoveryActivityEvent[],
  relatedRecordType: NonNullable<RecoveryActivityEvent["relatedRecordType"]>,
  relatedRecordId: BusinessCenterEntityId,
) {
  return events.filter((event) => event.relatedRecordType === relatedRecordType && event.relatedRecordId === relatedRecordId);
}

export function filterRecoveryActivityEventsByVisibility(
  events: RecoveryActivityEvent[],
  visibility: RecoveryActivityEvent["visibility"],
) {
  return events.filter((event) => event.visibility === visibility);
}

export function filterRecoveryActivityEventsByEventType(events: RecoveryActivityEvent[], eventType: RecoveryActivityEventType) {
  return events.filter((event) => event.eventType === eventType);
}

export function buildARRecoverySectionLinks(section: ARRecoverySectionKey): ARRecoverySectionLink[] {
  return RECOVERY_NAVIGATION.map((link) => ({
    ...link,
    state: link.key === section ? "Active" : link.state === "Active" ? "Ready" : link.state,
  }));
}

export function buildARRecoveryDashboardSnapshot(companyId: BusinessCenterEntityId = ACTIVE_AR_RECOVERY_COMPANY_ID): ARRecoveryDashboardSnapshot {
  const workspace = loadARRecoveryWorkspace(companyId);
  const totalOpenAR = workspace.accounts.reduce((sum, account) => sum + account.openBalanceCents, 0);
  const currentBalance = bucketBalance(workspace.agingBuckets, "Current");
  const balance1to30 = bucketBalance(workspace.agingBuckets, "1-30 Days");
  const balance31to60 = bucketBalance(workspace.agingBuckets, "31-60 Days");
  const balance61to90 = bucketBalance(workspace.agingBuckets, "61-90 Days");
  const balance90Plus = bucketBalance(workspace.agingBuckets, "90+ Days");
  const recoveryDollars = workspace.collectors.reduce((sum, collector) => sum + collector.recoveryCents, 0);
  const promisesDueTodayCount = workspace.promises.filter((promise) => promise.status === "Due Today").length;
  const promisesUpcomingCount = workspace.promises.filter((promise) => isUpcomingPromiseStatus(promise.status)).length;
  const promisesBrokenCount = workspace.promises.filter((promise) => isBrokenPromiseStatus(promise.status)).length;
  const promisesPartialCount = workspace.promises.filter((promise) => promise.status === "Partial").length;
  const promisesCompletedCount = workspace.promises.filter((promise) => isCompletedPromiseStatus(promise.status)).length;
  const ticklerTodayCount = workspace.ticklers.filter((tickler) => tickler.status === "Due Today").length;
  const ticklerOverdueCount = workspace.ticklers.filter((tickler) => tickler.status === "Overdue").length;
  const ticklerUpcomingCount = workspace.ticklers.filter((tickler) => tickler.status === "Upcoming").length;
  const ticklerCriticalCount = workspace.ticklers.filter((tickler) => tickler.priority === "Critical").length;
  const ticklerHighCount = workspace.ticklers.filter((tickler) => tickler.priority === "High").length;
  const accountsRequiringAction = workspace.accounts.filter((account) => account.recoveryStatus === "Escalated" || account.escalationStatus !== "None").length;
  const highRiskAccounts = workspace.accounts.filter((account) => account.recoveryStatus === "Escalated" || account.escalationStatus === "Director Review").length;
  const brokenPromises = promisesBrokenCount;
  const followUpsDueToday = ticklerTodayCount + promisesDueTodayCount;
  const overdueTicklers = ticklerOverdueCount;
  const escalationsNeeded = workspace.accounts.filter((account) => account.escalationStatus !== "None").length;
  const promiseToPayAmount = workspace.promises
    .filter((promise) => !isCompletedPromiseStatus(promise.status))
    .reduce((sum, promise) => sum + promise.amountCents, 0);
  const recoveryRate = totalOpenAR === 0 ? 0 : recoveryDollars / totalOpenAR;

  return {
    company: workspace.company,
    branding: workspace.branding,
    totalOpenAR,
    currentBalance,
    balance1to30,
    balance31to60,
    balance61to90,
    balance90Plus,
    recoveryDollars,
    recoveryRate,
    promiseToPayAmount,
    accountsAssigned: workspace.collectors.reduce((sum, collector) => sum + collector.accountsAssigned, 0),
    accountsRequiringAction,
    followUpsDueToday,
    overdueTicklers,
    brokenPromises,
    highRiskAccounts,
    escalationsNeeded,
    newAccountsImported: workspace.newAccountsImported,
    collectionLettersPending: workspace.collectionLettersPending,
    statementsPending: workspace.statementsPending,
    ticklerTodayCount,
    ticklerOverdueCount,
    ticklerUpcomingCount,
    ticklerCriticalCount,
    ticklerHighCount,
    promisesDueTodayCount,
    promisesUpcomingCount,
    promisesBrokenCount,
    promisesPartialCount,
    promisesCompletedCount,
    companyScopeExample: buildCompanyScopeExample(workspace),
  };
}

export function buildARRecoveryPromiseCenterSnapshot(
  companyId: BusinessCenterEntityId = ACTIVE_AR_RECOVERY_COMPANY_ID,
  search = "",
  selectedPromiseId?: BusinessCenterEntityId | null,
  selectedTab: ARRecoveryPromiseTabKey = "overview",
  workspaceOverride?: ReturnType<typeof loadARRecoveryWorkspace>,
): ARRecoveryPromiseCenterSnapshot {
  const workspace = workspaceOverride || loadARRecoveryWorkspace(companyId);
  const normalized = search.trim().toLowerCase();
  const resolveCanonicalPromise = (promiseId?: BusinessCenterEntityId | null) =>
    promiseId ? workspace.promises.find((promise) => promise.id === promiseId) || null : null;
  const filteredPromises = workspace.promises
    .filter((promise) =>
      [
        promise.customerName,
        promise.customerNumber,
        promise.clientCompany,
        promise.promiseType,
        promise.collector,
        promise.manager,
        promise.status,
        promise.notes,
        promise.collectorNotes,
        promise.customerNotes,
        promise.managerNotes,
        promise.escalationNotes,
        promise.paymentNotes,
        promise.invoiceLabels.join(" "),
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalized),
    )
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  const selectedPromise = resolveCanonicalPromise(selectedPromiseId) || filteredPromises[0] || null;
  const today = new Date().toISOString().slice(0, 10);
  const openPromisesCount = workspace.promises.filter((promise) => !isClosedPromiseStatus(promise.status)).length;
  const dueTodayCount = workspace.promises.filter((promise) => promise.status === "Due Today" || promise.promiseDate === today).length;
  const brokenPromisesCount = workspace.promises.filter((promise) => isBrokenPromiseStatus(promise.status)).length;
  const pastDuePromisesCount = workspace.promises.filter((promise) => promise.promiseDate < today && !isClosedPromiseStatus(promise.status)).length;
  const promisesPaidCount = workspace.promises.filter((promise) => isCompletedPromiseStatus(promise.status)).length;
  const promiseRecoveryCents = workspace.promises
    .filter((promise) => isCompletedPromiseStatus(promise.status))
    .reduce((sum, promise) => sum + promise.amountCents, 0);
  const keptPromisedCount = workspace.promises.filter((promise) => isCompletedPromiseStatus(promise.status)).length;
  const promisesKeptRate = workspace.promises.length === 0 ? 0 : keptPromisedCount / workspace.promises.length;
  const installmentPlansActiveCount = workspace.promises.filter((promise) => promise.promiseType === "Installment Plan" && !isCompletedPromiseStatus(promise.status)).length;

  const typeLabels: ARRecoveryPromiseRecord["promiseType"][] = [
    "Single Payment",
    "Partial Payment",
    "Installment Plan",
    "Settlement Agreement",
    "Custom Promise",
  ];
  const promiseTypeCounts = typeLabels.map((label) => ({
    label,
    count: workspace.promises.filter((promise) => promise.promiseType === label).length,
    note:
      label === "Installment Plan"
        ? "Multi-payment commitment tracked over time."
        : label === "Settlement Agreement"
          ? "Structured promise for negotiated settlement."
          : label === "Partial Payment"
            ? "Partial remittance with a remainder due."
            : label === "Single Payment"
              ? "One-time payment commitment."
              : "Custom recovery promise.",
  }));

  const dashboardCards = [
    { label: "Open Promises", value: openPromisesCount, note: "Active commitments still in motion." },
    { label: "Due Today", value: dueTodayCount, note: "Promises that need attention right now." },
    { label: "Broken Promises", value: brokenPromisesCount, note: "Missed promise commitments." },
    { label: "Past Due Promises", value: pastDuePromisesCount, note: "Promised dates already passed." },
    { label: "Promises Paid", value: promisesPaidCount, note: "Closed and received promises." },
    { label: "Promises Kept %", value: `${Math.round(promisesKeptRate * 100)}%`, note: "Kept commitments versus total promises." },
    { label: "Promise Recovery $", value: formatARRecoveryCurrency(promiseRecoveryCents), note: "Collected through promise completion." },
    { label: "Installment Plans Active", value: installmentPlansActiveCount, note: "Multi-step commitments still open." },
  ];

  const collectorQueue = workspace.collectors.map((collector) => {
    const collectorPromises = workspace.promises.filter((promise) => promise.collector === collector.collectorName);
    return {
      collectorName: collector.collectorName,
      openPromises: collectorPromises.filter((promise) => !isClosedPromiseStatus(promise.status)).length,
      dueToday: collectorPromises.filter((promise) => promise.status === "Due Today" || promise.promiseDate === today).length,
      broken: collectorPromises.filter((promise) => isBrokenPromiseStatus(promise.status)).length,
      paid: collectorPromises.filter((promise) => isCompletedPromiseStatus(promise.status)).length,
      installmentPlans: collectorPromises.filter((promise) => promise.promiseType === "Installment Plan").length,
      recoveryCents: collectorPromises
        .filter((promise) => isCompletedPromiseStatus(promise.status))
        .reduce((sum, promise) => sum + promise.amountCents, 0),
    };
  });

  const managerMetrics = [
    { label: "Promise Recovery Dollars", value: formatARRecoveryCurrency(promiseRecoveryCents), note: "Revenue collected from kept promises." },
    { label: "Broken Promise Rate", value: `${Math.round((brokenPromisesCount / Math.max(workspace.promises.length, 1)) * 100)}%`, note: "Missed commitments requiring intervention." },
    { label: "Upcoming Promise Risk", value: pastDuePromisesCount + dueTodayCount, note: "Promises needing immediate attention." },
    { label: "Installment Plans Active", value: installmentPlansActiveCount, note: "Longer-running promise workflows." },
  ];

  const brokenPromiseSignals = [
    { title: "Missed Promise Date", detail: "Automatically flag promises when the due date passes without confirmation.", severity: "Critical" as const },
    { title: "Partial Payment Failure", detail: "Escalate if a partial remittance does not clear the remaining balance.", severity: "High" as const },
    { title: "Installment Failure", detail: "Trigger a review when one installment in a payment plan is missed.", severity: "High" as const },
    { title: "Settlement Failure", detail: "Recommend escalation when a settlement does not complete on schedule.", severity: "Critical" as const },
  ];

  const automationHooks = [
    { title: "Promise Created", detail: "Create a follow-up tickler and activity entry when a promise is saved." },
    { title: "Promise Due", detail: "Surface collector and manager reminders on the promise date." },
    { title: "Promise Broken", detail: "Mark the promise broken, generate follow-up work, and recommend escalation." },
    { title: "Installment Due", detail: "Queue installment reminders for upcoming payment dates." },
    { title: "Installment Missed", detail: "Open a missed-installment review task." },
    { title: "Promise Paid", detail: "Close the promise and log the payment completion event." },
    { title: "Manager Escalation", detail: "Notify leadership when risk thresholds are exceeded." },
  ];

  const portalCompatibility = [
    { title: "Open Promises", detail: "Future client portals can read company-scoped open commitments." },
    { title: "Broken Promises", detail: "Portal visibility can surface broken promise status without cross-company exposure." },
    { title: "Paid Promises", detail: "Closed promises can be shown as read-only history." },
    { title: "Promise Recovery Reports", detail: "Summary reporting can be reused by the portal reporting layer." },
    { title: "Promise Activity", detail: "Activity feed entries can flow into the portal timeline." },
  ];

  const activityFeed = workspace.promises
    .flatMap((promise) =>
      promise.activityFeed.map((entry) => ({
        id: entry.id,
        customerName: promise.customerName,
        title: entry.title,
        detail: entry.detail,
        kind: entry.kind,
        createdAt: entry.createdAt,
      })),
    )
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return {
    workspace,
    search,
    filteredPromises,
    selectedPromise,
    selectedTab,
    openPromisesCount,
    dueTodayCount,
    brokenPromisesCount,
    pastDuePromisesCount,
    promisesPaidCount,
    promisesKeptRate,
    promiseRecoveryCents,
    installmentPlansActiveCount,
    promiseTypeCounts,
    dashboardCards,
    collectorQueue,
    managerMetrics,
    brokenPromiseSignals,
    automationHooks,
    portalCompatibility,
    tabLabels: buildPromiseTabLabels(),
    activityFeed,
  };
}

export function buildARRecoveryCollectionLetterCenterSnapshot(
  companyId: BusinessCenterEntityId = ACTIVE_AR_RECOVERY_COMPANY_ID,
  search = "",
  selectedLetterId?: BusinessCenterEntityId | null,
  selectedTab: ARRecoveryLetterTabKey = "overview",
): ARRecoveryCollectionLetterCenterSnapshot {
  const workspace = loadARRecoveryWorkspace(companyId);
  const normalized = search.trim().toLowerCase();
  const filteredLetters = workspace.collectionLetters
    .filter((letter) =>
      [
        letter.customerName,
        letter.customerNumber,
        letter.clientCompany,
        letter.template,
        letter.status,
        letter.sentBy,
        letter.collector,
        letter.manager,
        letter.notes,
        letter.agingBucket,
        letter.deliveryStatus,
        letter.viewedStatus,
        letter.downloadedStatus,
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalized),
    )
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  const selectedLetter = filteredLetters.find((letter) => letter.id === selectedLetterId) || filteredLetters[0] || null;
  const sentCount = workspace.collectionLetters.filter((letter) => letter.status === "Sent").length;
  const queuedCount = workspace.collectionLetters.filter((letter) => letter.status === "Queued").length;
  const deliveredCount = workspace.collectionLetters.filter((letter) => letter.deliveryStatus === "Delivered").length;
  const viewedCount = workspace.collectionLetters.filter((letter) => letter.viewedStatus === "Viewed").length;
  const downloadedCount = workspace.collectionLetters.filter((letter) => letter.downloadedStatus === "Downloaded").length;
  const bulkReadyCount = workspace.collectionLetters.filter((letter) => letter.status === "Queued" || letter.status === "Draft").length;

  const dashboardCards = [
    { label: "Total Letters", value: workspace.collectionLetters.length, note: "Tenant-scoped letter records." },
    { label: "Sent", value: sentCount, note: "Letters already dispatched." },
    { label: "Queued", value: queuedCount, note: "Ready for bulk or scheduled send." },
    { label: "Delivered", value: deliveredCount, note: "Tracked as delivered to customers." },
    { label: "Viewed", value: viewedCount, note: "Opened or acknowledged letters." },
    { label: "Downloaded", value: downloadedCount, note: "Letter downloads tracked in history." },
    { label: "Bulk Ready", value: bulkReadyCount, note: "Templates available for batch actions." },
    { label: "Pending Letters", value: workspace.collectionLettersPending, note: "Operational queue from the dashboard." },
  ];

  const templates = letterTemplateOrder.map((template) => ({
    label: template,
    count: workspace.collectionLetters.filter((letter) => letter.template === template).length,
    note:
      template === "Broken Promise Letter"
        ? "Escalation-friendly outreach for missed commitments."
        : template === "Promise Reminder"
          ? "Gentle reminder used before the promise date."
          : template === "Demand Letter"
            ? "High-pressure collection notice for escalations."
            : template === "Settlement Offer"
              ? "Negotiated resolution template."
              : template === "Custom Letter Builder"
                ? "Tenant-configurable outreach builder."
                : "Operational recovery letter template.",
  }));

  const statusCounts = [
    { label: "Draft", count: workspace.collectionLetters.filter((letter) => letter.status === "Draft").length, note: "Being prepared for review." },
    { label: "Queued", count: queuedCount, note: "Ready to send in bulk or individually." },
    { label: "Sent", count: sentCount, note: "Already sent to the customer." },
    { label: "Delivered", count: deliveredCount, note: "Successfully delivered." },
    { label: "Viewed", count: viewedCount, note: "Viewed in the tracking stream." },
    { label: "Downloaded", count: downloadedCount, note: "Downloaded or archived." },
  ];

  const bulkActions = [
    { label: "Send One Account", description: "Send the selected letter to one account and keep the audit trail.", priority: "High" as const },
    { label: "Send Bulk Letters", description: "Send hundreds of letters filtered by customer, balance, aging, status, collector, or client.", priority: "Critical" as const },
    { label: "Generate Reminder Letters", description: "Turn open promises and follow-ups into scheduled reminder letters.", priority: "High" as const },
    { label: "Create Custom Letter", description: "Draft a tenant-ready custom recovery letter.", priority: "Medium" as const },
  ];

  const mergePreview = selectedLetter
    ? Object.entries(selectedLetter.mergeFields).map(([label, value]) => ({
        label: label === "businessName" ? "Business Name" : label === "dbaName" ? "DBA Name" : label === "contactName" ? "Contact Name" : label === "address" ? "Address" : label === "phone" ? "Phone" : label === "email" ? "Email" : "Website",
        value,
      }))
    : [];

  const trackingMetrics = [
    { label: "Delivery Status", value: selectedLetter?.deliveryStatus || "Pending", note: "Current delivery state for the selected letter." },
    { label: "Viewed Status", value: selectedLetter?.viewedStatus || "Pending", note: "Viewed tracking for the selected record." },
    { label: "Downloaded Status", value: selectedLetter?.downloadedStatus || "Pending", note: "Download tracking for the selected record." },
    { label: "Collector", value: selectedLetter?.collector || "Unassigned", note: "Owner for the recovery workflow." },
    { label: "Manager", value: selectedLetter?.manager || "Unassigned", note: "Leadership owner for review." },
  ];

  const activityFeed = workspace.collectionLetters
    .flatMap((letter) => [
      { id: `${letter.id}-created`, title: `${letter.template} created`, detail: `${letter.customerName} • ${letter.status}`, createdAt: letter.createdAt },
      ...(letter.sentAt
        ? [
            {
              id: `${letter.id}-sent`,
              title: `${letter.template} sent`,
              detail: `${letter.sentBy} • ${letter.deliveryStatus}`,
              createdAt: letter.sentAt,
            },
          ]
        : []),
      { id: `${letter.id}-updated`, title: `${letter.template} updated`, detail: letter.notes, createdAt: letter.updatedAt },
    ])
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return {
    workspace,
    search,
    filteredLetters,
    selectedLetter,
    selectedTab,
    dashboardCards,
    templates,
    statusCounts,
    bulkActions,
    mergePreview,
    trackingMetrics,
    activityFeed,
    tabLabels: buildLetterCenterTabLabels(),
  };
}

export function buildARRecoveryStatementCenterSnapshot(
  companyId: BusinessCenterEntityId = ACTIVE_AR_RECOVERY_COMPANY_ID,
  search = "",
  selectedStatementId?: BusinessCenterEntityId | null,
  selectedTab: ARRecoveryLetterTabKey = "overview",
  preparedWorkspace?: ARRecoveryWorkspace,
): ARRecoveryStatementCenterSnapshot {
  const workspace = preparedWorkspace && preparedWorkspace.company.id === companyId
    ? preparedWorkspace
    : loadARRecoveryWorkspace(companyId);
  const normalized = search.trim().toLowerCase();
  const filteredStatements = workspace.statements
    .filter((statement) =>
      [
        statement.customerName,
        statement.customerNumber,
        statement.clientCompany,
        statement.statementNumber,
        statement.status,
        statement.sentBy,
        statement.collector,
        statement.manager,
        statement.notes,
        statement.deliveryStatus,
        statement.openedStatus,
        statement.downloadedStatus,
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalized),
    )
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  const selectedStatement = filteredStatements.find((statement) => statement.id === selectedStatementId) || filteredStatements[0] || null;
  const sentCount = workspace.statements.filter((statement) => statement.status === "Sent").length;
  const queuedCount = workspace.statements.filter((statement) => statement.status === "Queued").length;
  const deliveredCount = workspace.statements.filter((statement) => statement.deliveryStatus === "Delivered").length;
  const openedCount = workspace.statements.filter((statement) => statement.openedStatus === "Opened").length;
  const downloadedCount = workspace.statements.filter((statement) => statement.downloadedStatus === "Downloaded").length;

  const dashboardCards = [
    { label: "Total Statements", value: workspace.statements.length, note: "Tenant-scoped statement records." },
    { label: "Sent", value: sentCount, note: "Already issued to customers." },
    { label: "Queued", value: queuedCount, note: "Ready to send or approve." },
    { label: "Delivered", value: deliveredCount, note: "Tracked as delivered." },
    { label: "Opened", value: openedCount, note: "Viewed by the customer." },
    { label: "Downloaded", value: downloadedCount, note: "Downloaded or archived." },
    { label: "Pending Statements", value: workspace.statementsPending, note: "Operational queue from the dashboard." },
    { label: "Open Invoice Count", value: workspace.statements.reduce((sum, statement) => sum + statement.openInvoiceCount, 0), note: "Invoice volume represented in statements." },
  ];

  const statusCounts = [
    { label: "Created", count: workspace.statements.filter((statement) => statement.status === "Created").length, note: "Created and not yet queued." },
    { label: "Queued", count: queuedCount, note: "Waiting to be sent." },
    { label: "Sent", count: sentCount, note: "Already issued." },
    { label: "Delivered", count: deliveredCount, note: "Delivery tracked." },
    { label: "Opened", count: openedCount, note: "Customer opened the statement." },
    { label: "Downloaded", count: downloadedCount, note: "Statement downloaded or archived." },
  ];

  const bulkActions = [
    { label: "Send One Statement", description: "Send the selected statement to one customer account.", priority: "High" as const },
    { label: "Send Bulk Statements", description: "Send statements filtered by customer, balance, aging, status, collector, or client.", priority: "Critical" as const },
    { label: "Schedule Statement Batch", description: "Queue scheduled statement runs for large portfolios.", priority: "High" as const },
    { label: "Create Statement Template", description: "Prepare a new tenant-branded statement format.", priority: "Medium" as const },
  ];

  const deliveryMetrics = [
    { label: "Delivery Status", value: selectedStatement?.deliveryStatus || "Pending", note: "Current delivery state for the selected statement." },
    { label: "Opened Status", value: selectedStatement?.openedStatus || "Pending", note: "Open tracking for the selected record." },
    { label: "Downloaded Status", value: selectedStatement?.downloadedStatus || "Pending", note: "Download tracking for the selected record." },
    { label: "Collector", value: selectedStatement?.collector || "Unassigned", note: "Owner for the recovery workflow." },
    { label: "Manager", value: selectedStatement?.manager || "Unassigned", note: "Leadership owner for review." },
  ];

  const lineItemPreview = selectedStatement?.lineItems || [];
  const activityFeed = workspace.statements
    .flatMap((statement) => [
      { id: `${statement.id}-created`, title: `${statement.statementNumber} created`, detail: `${statement.customerName} • ${statement.status}`, createdAt: statement.createdAt },
      ...(statement.sentAt
        ? [
            {
              id: `${statement.id}-sent`,
              title: `${statement.statementNumber} sent`,
              detail: `${statement.sentBy} • ${statement.deliveryStatus}`,
              createdAt: statement.sentAt,
            },
          ]
        : []),
      { id: `${statement.id}-updated`, title: `${statement.statementNumber} updated`, detail: statement.notes, createdAt: statement.updatedAt },
    ])
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return {
    workspace,
    search,
    filteredStatements,
    selectedStatement,
    selectedTab,
    dashboardCards,
    statusCounts,
    bulkActions,
    deliveryMetrics,
    lineItemPreview,
    activityFeed,
    tabLabels: buildStatementCenterTabLabels(),
  };
}

export function buildARRecoveryCommercializationSnapshot(
  companyId: BusinessCenterEntityId = ACTIVE_AR_RECOVERY_COMPANY_ID,
  selectedTab: ARRecoveryCommercializationTabKey = "overview",
): ARRecoveryCommercializationSnapshot {
  const workspace = loadARRecoveryWorkspace(companyId);
  const readinessScore = Math.min(100, 70 + workspace.collectionLetters.length * 2 + workspace.statements.length * 2 + workspace.promises.length + workspace.ticklers.length);

  const readinessCards = [
    { label: "Internal Operations", value: "Ready", note: "Core recovery workflows are live." },
    { label: "Recovery Services", value: "Ready", note: "Service delivery flows are established." },
    { label: "Client Portal", value: "Ready", note: "Company-scoped portal is in place." },
    { label: "White Label", value: "Ready", note: "Branding and tenant identity are supported." },
    { label: "SaaS", value: "Ready", note: "Multi-tenant routes and data scaffolding exist." },
    { label: "Enterprise", value: `${readinessScore}%`, note: "Deployment packaging and onboarding readiness." },
  ];

  const launchSequence = [
    {
      title: "Recovery Service Clients",
      detail: "Operational onboarding for service-led clients that need AR recovery execution and reporting.",
      steps: ["Client Information", "Contacts", "Service Package", "Upload Aging", "Upload Customer List", "Upload Open Invoices", "Letter Preferences", "Portal Setup", "Collector Assignment", "Launch"],
    },
    {
      title: "Software Clients",
      detail: "Self-serve onboarding for software licensing, white-label deployments, and tenant setup.",
      steps: ["Company Setup", "Branding", "User Creation", "Role Assignment", "Portal Settings", "Automation Settings", "Import Data", "Go Live"],
    },
  ];

  const deploymentControls = [
    { title: "Logo", value: workspace.clientProfile.businessName, note: "Tenant logo can be loaded in the deployment center." },
    { title: "Favicon", value: workspace.branding.displayName, note: "Browser icon and portal badge can be swapped per tenant." },
    { title: "Company Name", value: workspace.clientProfile.businessName, note: "Displayed across login, dashboard, and communications." },
    { title: "DBA", value: workspace.clientProfile.dbaName, note: "Used in letterhead and portal branding." },
    { title: "Color Palette", value: `${workspace.branding.primaryColor} / ${workspace.branding.accentColor}`, note: "Brand colors are ready for tenant-specific deployment." },
    { title: "Portal Name", value: `${workspace.branding.displayName} Portal`, note: "Portal shell branding is tenant-aware." },
    { title: "Portal URL", value: workspace.clientProfile.portalUrl, note: "Tenant URL can be mapped to custom domains later." },
  ];

  const packageCatalog = [
    { name: "Recovery Service Basic", category: "Recovery Service", pricing: "$2,500/mo", features: ["Core recovery workflow", "Client portal", "Letter/statement tracking"], limits: "1 team, 5 users", activeClients: 1, status: "Available" as const },
    { name: "Recovery Service Professional", category: "Recovery Service", pricing: "$5,500/mo", features: ["Advanced reporting", "Smart ticklers", "Manager oversight"], limits: "3 teams, 20 users", activeClients: 2, status: "Available" as const },
    { name: "Recovery Service Enterprise", category: "Recovery Service", pricing: "$11,000/mo", features: ["White-label branding", "Automation audit", "Priority support"], limits: "Unlimited users", activeClients: 0, status: "Available" as const },
    { name: "Recovery OS Starter", category: "Software", pricing: "$1,500/mo", features: ["Single tenant", "Portal shell", "Basic onboarding"], limits: "1 company, 10 users", activeClients: 1, status: "Active" as const },
    { name: "Recovery OS Professional", category: "Software", pricing: "$4,000/mo", features: ["Automation settings", "Package controls", "Documentation center"], limits: "3 companies, 50 users", activeClients: 1, status: "Available" as const },
    { name: "White Label Enterprise", category: "White Label", pricing: "$12,000/mo", features: ["Custom domain", "Branding suite", "Tenant deployment"], limits: "Multi-brand deployment", activeClients: 0, status: "Available" as const },
  ];

  const clientSuccessMetrics = [
    { label: "Onboarding Status", value: "Ready", note: "Client onboarding flow is available." },
    { label: "Go-Live Status", value: "Ready", note: "Production launch path is mapped." },
    { label: "Training Status", value: "Ready", note: "Training materials can be attached to the deployment flow." },
    { label: "Support Requests", value: workspace.collectionLettersPending + workspace.statementsPending, note: "Pending client-facing follow-up workload." },
    { label: "Success Notes", value: "Seeded", note: "Client success notes can be attached per tenant." },
    { label: "Health Score", value: `${Math.min(100, readinessScore + 5)}%`, note: "Commercial readiness proxy score." },
    { label: "Renewal Date", value: isoDate(365), note: "Forward renewal checkpoint." },
  ];

  const supportQueue = [
    { title: "Client Issue", detail: "A client cannot access the portal after launch.", severity: "High" as const, linkedTo: workspace.clientProfile.businessName },
    { title: "Feature Request", detail: "Request for a new letter template or onboarding step.", severity: "Medium" as const, linkedTo: "Documentation Center" },
    { title: "Escalation", detail: "Deployment or branding change needs leadership review.", severity: "Critical" as const, linkedTo: workspace.clientProfile.escalationContact },
    { title: "Software Issue", detail: "User assignment or domain mapping needs assistance.", severity: "High" as const, linkedTo: workspace.clientProfile.portalUrl },
  ];

  const documentationLibrary = [
    { title: "User Guides", category: "Documentation", description: "Step-by-step guidance for recovery teams and admins.", status: "Ready" },
    { title: "Client Guides", category: "Documentation", description: "Client-facing portal and workflow guides.", status: "Ready" },
    { title: "Admin Guides", category: "Documentation", description: "Internal operations and tenant setup guides.", status: "Ready" },
    { title: "White Label Guides", category: "Documentation", description: "Tenant branding and deployment notes.", status: "Ready" },
    { title: "Training Materials", category: "Training", description: "Collector and manager onboarding assets.", status: "Ready" },
    { title: "Video Resources", category: "Training", description: "Future training video library foundation.", status: "Planned" },
    { title: "FAQ Library", category: "Knowledge Base", description: "Frequently asked questions for support and onboarding.", status: "Ready" },
    { title: "Knowledge Base", category: "Knowledge Base", description: "Searchable support documentation foundation.", status: "Ready" },
  ];

  const trainingPrograms = [
    { title: "Collector Training", audience: "Recovery Service Teams", completion: "90%", certification: "Ready" },
    { title: "Manager Training", audience: "Recovery Managers", completion: "85%", certification: "Ready" },
    { title: "Client Training", audience: "Portal Clients", completion: "75%", certification: "Ready" },
    { title: "Software User Training", audience: "White Label Clients", completion: "80%", certification: "Ready" },
  ];

  const automationAudit = [
    { trigger: "New Client", status: "Ready" as const, detail: "Onboarding workflow can trigger tenant provisioning." },
    { trigger: "New User", status: "Ready" as const, detail: "Role assignment and portal access flow can fire." },
    { trigger: "New Account", status: "Ready" as const, detail: "Account import can create follow-up work." },
    { trigger: "New Promise", status: "Ready" as const, detail: "Promise creation can attach follow-up ticklers." },
    { trigger: "Broken Promise", status: "Ready" as const, detail: "Broken commitments can escalate automatically." },
    { trigger: "Escalation", status: "Ready" as const, detail: "Manager review and notifications are supported." },
    { trigger: "Letter Sent", status: "Ready" as const, detail: "Letters can queue activity and future reminders." },
    { trigger: "Statement Sent", status: "Ready" as const, detail: "Statements can create follow-up tasks." },
    { trigger: "Tickler Created", status: "Ready" as const, detail: "Ticklers can feed the collector queues." },
    { trigger: "Aging Threshold Reached", status: "Needs Review" as const, detail: "Bucket-based automation should be hardened next." },
  ];

  const launchChecklist = [
    { label: "Onboarding Center", completed: true, note: "Production client onboarding workflows are mapped." },
    { label: "White-Label Deployment Center", completed: true, note: "Branding and domain settings are ready for tenants." },
    { label: "Package Management Center", completed: true, note: "Service, software, and white-label packages are seeded." },
    { label: "Client Success Center", completed: true, note: "Health, training, and renewal tracking are represented." },
    { label: "Support Center", completed: true, note: "Issues, feature requests, and escalations are ready." },
    { label: "Documentation Center", completed: true, note: "Guides, KB, and training materials are organized." },
    { label: "Training Center", completed: true, note: "Training tracks and certifications are represented." },
    { label: "Automation Audit", completed: false, note: "Aging threshold automation hardening remains." },
  ];

  const targetIndustries = [
    { label: "Accounts Receivable Services", reason: "Best fit for recovery teams and managed AR operations." },
    { label: "SaaS / White Label Software", reason: "Supports licensing, branding, and tenant onboarding." },
    { label: "Professional Services", reason: "Can package consulting, onboarding, and client reporting." },
    { label: "Multi-Location Businesses", reason: "Tenant-scoped portal and records work well for multiple operating units." },
  ];

  return {
    workspace,
    selectedTab,
    readinessScore,
    readinessCards,
    launchSequence,
    deploymentControls,
    packageCatalog,
    clientSuccessMetrics,
    supportQueue,
    documentationLibrary,
    trainingPrograms,
    automationAudit,
    launchChecklist,
    targetIndustries,
    tabLabels: buildCommercializationTabLabels(),
  };
}

export function buildARRecoveryCustomerAccountsSnapshot(
  companyId: BusinessCenterEntityId = ACTIVE_AR_RECOVERY_COMPANY_ID,
  search = "",
  selectedAccountId?: BusinessCenterEntityId | null,
  selectedTab: ARRecoveryAccountTabKey = "overview",
  projectedWorkspace?: ARRecoveryWorkspace,
): ARRecoveryCustomerAccountsSnapshot {
  const workspace = projectedWorkspace?.company.id === companyId ? projectedWorkspace : loadARRecoveryWorkspace(companyId);
  const normalized = search.trim().toLowerCase();
  const filteredAccounts = workspace.accounts.filter((account) =>
    [
      account.customerName,
      account.customerNumber,
      account.contactName,
      account.contactEmail,
      account.assignedCollector,
      account.agingSummary,
      account.recoveryStatus,
      account.escalationStatus,
      account.notes,
      `${account.customerNumber}-INV-01`,
      `${account.customerNumber}-INV-02`,
      `${account.customerNumber}-INV-03`,
      `${account.customerNumber}-INV-04`,
    ]
      .join(" ")
      .toLowerCase()
      .includes(normalized),
  );
  const selectedAccount = filteredAccounts.find((account) => account.id === selectedAccountId) || filteredAccounts[0] || null;
  const totalOpenBalanceCents = filteredAccounts.reduce((sum, account) => sum + account.openBalanceCents, 0);
  const riskAccountCount = filteredAccounts.filter((account) => account.recoveryStatus === "Escalated" || account.escalationStatus !== "None").length;
  const actionReadyAccountCount = filteredAccounts.filter((account) => account.recoveryStatus !== "Resolved").length;
  const nextActionDate = selectedAccount ? getNextActionDate(selectedAccount) : null;

  return {
    workspace,
    search,
    filteredAccounts,
    selectedAccount,
    selectedTab,
    totalOpenBalanceCents,
    riskAccountCount,
    actionReadyAccountCount,
    nextActionDate,
    tabLabels: buildAccountTabLabels(),
  };
}

export function buildARRecoveryTicklerDashboardSnapshot(
  companyId: BusinessCenterEntityId = ACTIVE_AR_RECOVERY_COMPANY_ID,
): ARRecoveryTicklerDashboardSnapshot {
  const workspace = loadARRecoveryWorkspace(companyId);
  const dueToday = workspace.ticklers.filter((tickler) => tickler.status === "Due Today");
  const overdue = workspace.ticklers.filter((tickler) => tickler.status === "Overdue");
  const upcoming = workspace.ticklers.filter((tickler) => tickler.status === "Upcoming");
  const critical = workspace.ticklers.filter((tickler) => tickler.priority === "Critical");
  const assignedToMe = workspace.ticklers.filter((tickler) => tickler.assignedCollector === workspace.collectors[0]?.collectorName);
  const assignedToTeam = workspace.ticklers.filter((tickler) => tickler.assignedCollector === workspace.collectors[1]?.collectorName);
  const brokenPromiseFollowUps = workspace.ticklers.filter((tickler) => tickler.title.toLowerCase().includes("promise") || tickler.ticklerType === "Promise Follow-Up");
  const highRiskFollowUps = workspace.ticklers.filter((tickler) => tickler.priority === "Critical" || tickler.priority === "High");

  const collectorQueue = workspace.collectors.map((collector) => {
    const collectorTicklers = workspace.ticklers.filter((tickler) => tickler.assignedCollector === collector.collectorName);
    return {
      collectorName: collector.collectorName,
      dueAccounts: collector.accountsAssigned,
      overdueAccounts: collectorTicklers.filter((tickler) => tickler.status === "Overdue").length,
      todayCalls: Math.max(1, Math.round(collector.callsCompleted * 0.36)),
      todayEmails: Math.max(1, Math.round(collector.emailsSent * 0.28)),
      todayPromises: Math.max(0, workspace.promises.filter((promise) => promise.status === "Due Today" || promise.status === "Upcoming").length - 1),
      todayFollowUps: collectorTicklers.filter((tickler) => tickler.status === "Due Today").length,
      todayEscalations: collectorTicklers.filter((tickler) => tickler.ticklerType === "Escalation").length,
    };
  });

  const nextBestActions = [
    { label: "Call Customer", description: "Use when a promise is due or a collector needs to establish the next commitment.", priority: "Critical" as const },
    { label: "Send Statement", description: "Use when the account needs a fresh balance reminder.", priority: "High" as const },
    { label: "Send Collection Letter", description: "Use after repeated missed follow-ups or when escalation is trending up.", priority: "High" as const },
    { label: "Review Promise", description: "Use when a promise date is close or the commitment status changed.", priority: "Medium" as const },
    { label: "Escalate Account", description: "Use for accounts that have crossed review thresholds.", priority: "Critical" as const },
    { label: "Assign Manager Review", description: "Use to route complex accounts to leadership.", priority: "High" as const },
    { label: "Mark Resolved", description: "Use when collection work is complete and the account is closed out.", priority: "Low" as const },
  ];

  const automaticTriggers = [
    { title: "Promise Created", detail: "Create a follow-up tickler whenever a promise is saved." },
    { title: "Letter Sent", detail: "Add a reminder to verify delivery and response after a letter goes out." },
    { title: "Statement Sent", detail: "Create a follow-up after a statement is delivered." },
    { title: "No Activity Exists", detail: "Queue a reminder when an account has gone quiet too long." },
    { title: "Aging Threshold Reached", detail: "Auto-create a tickler when the balance moves into a new aging bucket." },
    { title: "Broken Promise Detected", detail: "Escalate the queue as soon as a promise is marked broken." },
    { title: "Manager Escalation Requested", detail: "Open a manager-review reminder when escalation is requested." },
  ];

  const managerMetrics = [
    { label: "Collector Workload", value: workspace.collectors.reduce((sum, collector) => sum + collector.accountsAssigned, 0), note: "Accounts assigned across the team." },
    { label: "Overdue Ticklers", value: overdue.length, note: "Immediate follow-up risk." },
    { label: "Broken Promises", value: workspace.promises.filter((promise) => promise.status === "Broken").length, note: "Missed commitments needing attention." },
    { label: "Recovery Pipeline", value: formatARRecoveryCurrency(workspace.accounts.reduce((sum, account) => sum + account.openBalanceCents, 0)), note: "Open AR still in play." },
    { label: "Activity Compliance", value: `${Math.round((workspace.activity.length / Math.max(workspace.accounts.length, 1)) * 100)}%`, note: "Activity density across the queue." },
    { label: "Accounts Not Worked", value: workspace.accounts.filter((account) => account.recoveryStatus === "Current").length, note: "Newer accounts awaiting outreach." },
  ];

  return {
    workspace,
    totalTicklers: workspace.ticklers.length,
    dueToday,
    overdue,
    upcoming,
    critical,
    assignedToMe,
    assignedToTeam,
    brokenPromiseFollowUps,
    highRiskFollowUps,
    collectorQueue,
    nextBestActions,
    automaticTriggers,
    managerMetrics,
  };
}

export function buildARRecoveryNavigation(section: ARRecoverySectionKey) {
  return buildARRecoverySectionLinks(section);
}

export function formatARRecoveryCurrency(cents = 0) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format((cents || 0) / 100);
}

export function formatARRecoveryPercent(value = 0) {
  return `${Math.round((value || 0) * 100)}%`;
}

function bucketBalance(buckets: ARRecoveryAgingBucketRecord[], label: ARRecoveryAgingBucketRecord["label"]) {
  return buckets.find((bucket) => bucket.label === label)?.balanceCents || 0;
}

const letterTemplateOrder: ARRecoveryCollectionLetterRecord["template"][] = [
  "Friendly Reminder",
  "Past Due Notice",
  "Second Notice",
  "Urgent Payment Request",
  "Demand Letter",
  "Final Notice",
  "Promise Reminder",
  "Broken Promise Letter",
  "Settlement Offer",
  "Account Review Notice",
  "Custom Letter Builder",
];

function buildLetterCenterTabLabels() {
  return [
    { key: "overview" as const, label: "Overview", description: "Letter center summary and workflow controls." },
    { key: "template" as const, label: "Templates", description: "Template coverage and client defaults." },
    { key: "tracking" as const, label: "Tracking", description: "Delivery, view, and download tracking." },
    { key: "bulk" as const, label: "Bulk Actions", description: "Single and bulk send workflows." },
    { key: "activity" as const, label: "Activity Feed", description: "Chronological letter activity stream." },
    { key: "account" as const, label: "Account Info", description: "Account-linked merge data and client branding." },
    { key: "history" as const, label: "Letter History", description: "Permanent letter timeline." },
  ];
}

function buildStatementCenterTabLabels() {
  return [
    { key: "overview" as const, label: "Overview", description: "Statement center summary and workflow controls." },
    { key: "template" as const, label: "Layouts", description: "Statement layout coverage and client defaults." },
    { key: "tracking" as const, label: "Tracking", description: "Delivery, open, and download tracking." },
    { key: "bulk" as const, label: "Bulk Actions", description: "Single and bulk statement workflows." },
    { key: "activity" as const, label: "Activity Feed", description: "Chronological statement activity stream." },
    { key: "account" as const, label: "Account Info", description: "Account-linked merge data and client branding." },
    { key: "history" as const, label: "Statement History", description: "Permanent statement timeline." },
  ];
}

function buildCommercializationTabLabels() {
  return [
    { key: "overview" as const, label: "Overview", description: "Commercial readiness score and launch status." },
    { key: "onboarding" as const, label: "Client Onboarding", description: "Service and software onboarding flows." },
    { key: "deployment" as const, label: "Deployment", description: "White-label branding and tenant deployment controls." },
    { key: "packages" as const, label: "Packages", description: "Service, software, and white-label package catalog." },
    { key: "success" as const, label: "Client Success", description: "Health, training, and renewal visibility." },
    { key: "support" as const, label: "Support", description: "Tickets, client issues, and escalations." },
    { key: "documentation" as const, label: "Documentation", description: "Guides, FAQ, knowledge base, and training assets." },
    { key: "training" as const, label: "Training", description: "Collector, manager, client, and software training." },
    { key: "automation" as const, label: "Automation Audit", description: "Workflow triggers and readiness status." },
    { key: "readiness" as const, label: "Readiness Review", description: "Launch checklist, target industries, and next steps." },
  ];
}

function buildAccountTabLabels() {
  return [
    { key: "overview" as const, label: "Overview", description: "Core account summary and risk details." },
    { key: "invoices" as const, label: "Open Invoices", description: "Open invoices and billing context." },
    { key: "activity" as const, label: "Timeline", description: "Unified calls, notes, promises, ticklers, letters, and status changes." },
    { key: "notes" as const, label: "Notes", description: "Internal, customer, promise, and escalation notes." },
    { key: "communications" as const, label: "Communications", description: "Internal AR team conversations tied to this customer account." },
    { key: "tasks" as const, label: "Tasks", description: "Operational assignments tied to this customer account." },
    { key: "letters" as const, label: "Letter Area", description: "Letter history and template placeholders." },
    { key: "statements" as const, label: "Statement Area", description: "Statement history and delivery placeholders." },
    { key: "promises" as const, label: "Promise Area", description: "Promise-to-pay events and follow-up links." },
    { key: "ticklers" as const, label: "Tickler Area", description: "Due, overdue, and upcoming reminders." },
    { key: "documents" as const, label: "Documents", description: "Company-scoped account documents." },
    { key: "history" as const, label: "Account History", description: "Chronological resolution history." },
  ];
}

function buildPromiseTabLabels() {
  return [
    { key: "overview" as const, label: "Overview", description: "Promise summary, status, and workflow controls." },
    { key: "account" as const, label: "Account Info", description: "Customer and account relationship data." },
    { key: "notes" as const, label: "Notes", description: "Collector, manager, customer, and payment notes." },
    { key: "activity" as const, label: "Activity Timeline", description: "Created, updated, reminder, payment, and escalation history." },
    { key: "payments" as const, label: "Payment History", description: "Installment schedule and payment progression." },
    { key: "letters" as const, label: "Related Letters", description: "Promise-related collection letters." },
    { key: "statements" as const, label: "Related Statements", description: "Statements tied to this promise." },
    { key: "ticklers" as const, label: "Related Ticklers", description: "Follow-up reminders and escalation tasks." },
    { key: "documents" as const, label: "Related Documents", description: "Promise attachments and account files." },
    { key: "history" as const, label: "Promise History", description: "Chronological promise lifecycle summary." },
  ];
}

function isBrokenPromiseStatus(status: ARRecoveryPromiseRecord["status"]) {
  return status === "Broken" || status === "Escalated";
}

function isCompletedPromiseStatus(status: ARRecoveryPromiseRecord["status"]) {
  return status === "Paid" || status === "Resolved" || status === "Completed";
}

function isClosedPromiseStatus(status: ARRecoveryPromiseRecord["status"]) {
  return isCompletedPromiseStatus(status) || status === "Cancelled";
}

function isUpcomingPromiseStatus(status: ARRecoveryPromiseRecord["status"]) {
  return status === "Open" || status === "Pending" || status === "Upcoming";
}

function getNextActionDate(account: ARRecoveryCustomerAccountRecord) {
  const noteDate = account.escalationStatus !== "None" ? 1 : account.recoveryStatus === "Current" ? 7 : 2;
  const date = new Date();
  date.setDate(date.getDate() + noteDate);
  return date.toISOString().slice(0, 10);
}

function buildCompanyScopeExample(workspace: ARRecoveryWorkspace) {
  const sampleAccount = workspace.accounts[0];
  return {
    customerName: sampleAccount?.customerName || "No account loaded",
    customerNumber: sampleAccount?.customerNumber || "AR-0000",
    contactName: sampleAccount?.contactName || "Not set",
    agingSummary: sampleAccount?.agingSummary || "Not set",
    openBalance: sampleAccount?.openBalanceCents || 0,
    assignedCollector: sampleAccount?.assignedCollector || "Unassigned",
    recoveryStatus: sampleAccount?.recoveryStatus || "Current",
    escalationStatus: sampleAccount?.escalationStatus || "None",
    notes: sampleAccount?.notes || "No notes available.",
  };
}

function isoDate(offsetDays: number) {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  return date.toISOString().slice(0, 10);
}

function timestampOffsetHours(offsetHours: number) {
  const date = new Date();
  date.setHours(date.getHours() + offsetHours);
  return date.toISOString();
}


